import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import {
  TrainingSessionContainer,
  PageSubtitle,
  PageHeader,
  FormSection,
  FormAreaWrapper,
  FormTitle,
  FormGrid,
  FormGroup,
  Label,
  Select,
  Input,
  INPUT_EMPTY_PLACEHOLDER_COLOR,
  TelemarkToggle,
  ToggleButton,
  SubmitButton,
  RecordsSection,
  RecordsTable,
  TableHeader,
  TableCell,
  TableRow,
} from '../TrainingSessions/styles';
import { PopulatedAthlete, PopulatedEquipment } from '../TrainingSessions/types';
import { PageTitle } from '../CoachDashboard/styles';
import { athleteAPI, equipmentAPI, trainingSessionAPI, bananaCupAPI } from '../../services/api';
import { hasPlanOrHigher } from '../../utils/subscriptionUtils';
import { Athlete as AthleteType } from '../../types/mongodb';

const QualificationButton = styled(SubmitButton)`
  background: #2d2d2d;
  border-color: #2d2d2d;

  &:hover:not(:disabled) {
    background: #404040;
    border-color: #404040;
  }
`;

// MongoDB types
interface Athlete {
  _id: string;
  firstName: string;
  lastName: string;
  subscription?: { plan?: string };
}

interface Equipment {
  _id: string;
  name: string;
  type: 'suit' | 'skis' | 'shoes';
  athleteId?: string | { _id: string; firstName: string; lastName: string };
}

// Competition session form data type
interface NewCompetitionSessionFormData {
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  weather: string;
  category: string;
  hillSize: string;
  ageCategory: string;
  athleteIds: string[];
}

// Competition jump record type
interface CompetitionJumpRecord {
  _id: string;
  athleteId: string;
  // Trial round data (optional, before first round)
  trialRoundStartingGate?: number;
  trialRoundDistance?: number;
  trialRoundWind?: string;
  trialRoundSpeed?: number;
  trialRoundTelemark?: boolean;
  trialRoundSuitId?: string;
  trialRoundSkisId?: string;
  trialRoundShoesId?: string;
  trialRoundBindingsId?: string;
  // First round data
  firstRoundStartingGate?: number;
  firstRoundDistance?: number;
  firstRoundWind?: string;
  firstRoundSpeed?: number;
  firstRoundTelemark?: boolean;
  firstRoundSuitId?: string;
  firstRoundSkisId?: string;
  firstRoundShoesId?: string;
  firstRoundBindingsId?: string;
  // Second round data
  secondRoundStartingGate?: number;
  secondRoundDistance?: number;
  secondRoundWind?: string;
  secondRoundSpeed?: number;
  secondRoundTelemark?: boolean;
  secondRoundSuitId?: string;
  secondRoundSkisId?: string;
  secondRoundShoesId?: string;
  secondRoundBindingsId?: string;
  // Result
  result?: number;
  // Legacy equipment fields (for backward compatibility, will be removed)
  suitId?: string;
  skisId?: string;
  shoesId?: string;
  bindingsId?: string;
  hillSize?: string;
  rating?: number;
  createdAt: string;
}

// Competition session record type
interface CompetitionSessionRecord {
  _id: string;
  trainingType: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  weather: string;
  category?: string;
  hillSize?: string;
  ageCategory?: string;
  athleteIds: string[];
  competitionJumps: CompetitionJumpRecord[];
  isQualification?: boolean;
  isPaused?: boolean;
  coachDescription?: string;
  isCompleted?: boolean;
  createdAt: string;
}

// Banana Cup jump record type
interface BananaCupJumpRecord {
  _id: string;
  athleteId: string | PopulatedAthlete;
  firstRoundStartingGate?: number;
  firstRoundDistance?: number;
  firstRoundWind?: string;
  firstRoundSpeed?: number;
  firstRoundTelemark?: boolean;
  secondRoundStartingGate?: number;
  secondRoundDistance?: number;
  secondRoundWind?: string;
  secondRoundSpeed?: number;
  secondRoundTelemark?: boolean;
  thirdRoundStartingGate?: number;
  thirdRoundDistance?: number;
  thirdRoundWind?: string;
  thirdRoundSpeed?: number;
  thirdRoundTelemark?: boolean;
  fourthRoundStartingGate?: number;
  fourthRoundDistance?: number;
  fourthRoundWind?: string;
  fourthRoundSpeed?: number;
  fourthRoundTelemark?: boolean;
  fifthRoundStartingGate?: number;
  fifthRoundDistance?: number;
  fifthRoundWind?: string;
  fifthRoundSpeed?: number;
  fifthRoundTelemark?: boolean;
  totalDistance?: number;
  position?: number;
  points?: number;
  suitId: string | PopulatedEquipment;
  skisId: string | PopulatedEquipment;
  shoesId?: string | PopulatedEquipment;
  hillSize?: string;
}

// Banana Cup competition session record type
interface BananaCupSessionRecord {
  _id: string;
  bananaCupId: string;
  bananaCupName: string;
  competitionNumber: number;
  date: string;
  startTime?: string;
  endTime?: string;
  location: string;
  weather: string;
  hillSize: string;
  jumps: BananaCupJumpRecord[];
  isCompleted: boolean;
  athleteIds: (string | PopulatedAthlete)[];
}

type SortField = 'date' | 'startTime' | 'endTime' | 'location' | 'weather' | 'category' | 'athlete' | 'distance' | 'avgDistance' | 'hillSize' | 'trialRound';
type BananaCupSortField = 'date' | 'startTime' | 'endTime' | 'location' | 'hillSize' | 'weather' | 'bananaCupName' | 'competitionNumber' | 'distance';
type SortDirection = 'asc' | 'desc';

interface CompetitionSessionsProps {
  athleteId?: string; // Optional prop to filter by athlete and lock selection
}

const CompetitionSessions: React.FC<CompetitionSessionsProps> = ({ athleteId: filterAthleteId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [competitionSessions, setCompetitionSessions] = useState<CompetitionSessionRecord[]>([]);
  const [bananaCupSessions, setBananaCupSessions] = useState<BananaCupSessionRecord[]>([]);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [expandedBananaCupSessions, setExpandedBananaCupSessions] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [bananaCupSortField, setBananaCupSortField] = useState<BananaCupSortField>('date');
  const [bananaCupSortDirection, setBananaCupSortDirection] = useState<SortDirection>('desc');
  const [competitionSortField, setCompetitionSortField] = useState<string>('');
  const [competitionSortDirection, setCompetitionSortDirection] = useState<'asc' | 'desc'>('asc');
  const [bananaCupJumpSortField, setBananaCupJumpSortField] = useState<string>('position');
  const [bananaCupJumpSortDirection, setBananaCupJumpSortDirection] = useState<'asc' | 'desc'>('asc');
  const [formData, setFormData] = useState<{
    athleteId: string;
    hillSize: string;
    // Trial round data
    trialRoundStartingGate: number;
    trialRoundDistance: number;
    trialRoundWind: string;
    trialRoundSpeed: number;
    trialRoundTelemark: boolean;
    trialRoundSuitId: string;
    trialRoundSkisId: string;
    trialRoundShoesId: string;
    trialRoundBindingsId: string;
    // First round data
    firstRoundStartingGate: number;
    firstRoundDistance: number;
    firstRoundWind: string;
    firstRoundSpeed: number;
    firstRoundTelemark: boolean;
    firstRoundSuitId: string;
    firstRoundSkisId: string;
    firstRoundShoesId: string;
    firstRoundBindingsId: string;
    // Second round data
    secondRoundStartingGate: number;
    secondRoundDistance: number;
    secondRoundWind: string;
    secondRoundSpeed: number;
    secondRoundTelemark: boolean;
    secondRoundSuitId: string;
    secondRoundSkisId: string;
    secondRoundShoesId: string;
    secondRoundBindingsId: string;
    // Result
    result: number;
    // Rating per round
    trialRoundRating: number;
    firstRoundRating: number;
    secondRoundRating: number;
  }>({
    athleteId: '',
    hillSize: '',
    trialRoundStartingGate: 0,
    trialRoundDistance: 0,
    trialRoundWind: '',
    trialRoundSpeed: 0,
    trialRoundTelemark: false,
    trialRoundSuitId: '',
    trialRoundSkisId: '',
    trialRoundShoesId: '',
    trialRoundBindingsId: '',
    firstRoundStartingGate: 0,
    firstRoundDistance: 0,
    firstRoundWind: '',
    firstRoundSpeed: 0,
    firstRoundTelemark: false,
    firstRoundSuitId: '',
    firstRoundSkisId: '',
    firstRoundShoesId: '',
    firstRoundBindingsId: '',
    secondRoundStartingGate: 0,
    secondRoundDistance: 0,
    secondRoundWind: '',
    secondRoundSpeed: 0,
    secondRoundTelemark: false,
    secondRoundSuitId: '',
    secondRoundSkisId: '',
    secondRoundShoesId: '',
    secondRoundBindingsId: '',
    result: 0,
    trialRoundRating: 0,
    firstRoundRating: 0,
    secondRoundRating: 0,
  });

  // Raw input values for decimal fields (to allow comma/dot input)
  const [rawInputValues, setRawInputValues] = useState<{
    trialRoundDistance: string;
    trialRoundSpeed: string;
    firstRoundDistance: string;
    firstRoundSpeed: string;
    secondRoundDistance: string;
    secondRoundSpeed: string;
  }>({
    trialRoundDistance: '',
    trialRoundSpeed: '',
    firstRoundDistance: '',
    firstRoundSpeed: '',
    secondRoundDistance: '',
    secondRoundSpeed: '',
  });
  
  // New competition session form state
  const [newCompetitionSessionData, setNewCompetitionSessionData] = useState<NewCompetitionSessionFormData>({
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    location: '',
    weather: '',
    category: '',
    hillSize: '',
    ageCategory: '',
    athleteIds: [],
  });
  
  // State to control visibility of the jump form
  const [showJumpForm, setShowJumpForm] = useState(false);
  // State to control if competition session is active
  const [isCompetitionSessionActive, setIsCompetitionSessionActive] = useState(false);
  const [activeSessionIsQualification, setActiveSessionIsQualification] = useState(false);
  // State to control visibility of the new competition session form
  const [showNewCompetitionSessionForm, setShowNewCompetitionSessionForm] = useState(false);
  const [isQualificationMode, setIsQualificationMode] = useState(false);
  const [coachDescription, setCoachDescription] = useState<string>('');
  
  // Edit state
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingJumpId, setEditingJumpId] = useState<string | null>(null);
  const [editingSessionData, setEditingSessionData] = useState<NewCompetitionSessionFormData | null>(null);
  
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoPopulatedFields, setAutoPopulatedFields] = useState<Set<string>>(new Set());
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [equipmentMessageField, setEquipmentMessageField] = useState<'suit' | 'skis' | 'shoes' | 'bindings' | null>(null);
  const [equipmentMessageSection, setEquipmentMessageSection] = useState<'trialRound' | 'firstRound' | 'secondRound' | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const editFormRef = useRef<HTMLDivElement>(null);
  const editJumpFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Open new competition form when navigated from Coach Home
  useEffect(() => {
    const state = location.state as { openNewCompetitionForm?: boolean } | null;
    if (state?.openNewCompetitionForm) {
      setShowNewCompetitionSessionForm(true);
      window.history.replaceState({}, document.title);
      setTimeout(() => editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [location.state]);

  useEffect(() => {
    // Fetch athletes, equipment, competition sessions, and Banana Cup data from MongoDB
    const fetchData = async () => {
      try {
        setIsLoading(true);
        let sessionsResponse;
        
        // If filterAthleteId is provided, fetch only that athlete's sessions
        if (filterAthleteId) {
          sessionsResponse = await trainingSessionAPI.getByAthlete(filterAthleteId, undefined, undefined, true); // Load all for competition sessions
        } else {
          sessionsResponse = await trainingSessionAPI.getAll(undefined, undefined, true); // Load all for competition sessions
        }
        
        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);
        
        const [athleteData, equipmentData, bananaCupData] = await Promise.all([
          athleteAPI.getAll(),
          equipmentAPI.getAll(),
          bananaCupAPI.getAll().catch(() => []), // Handle Banana Cup fetch errors gracefully
        ]);
        // Show all athletes; lock/unlock by plan in UI
        setAthletes(athleteData);
        setEquipment(equipmentData);
        // Filter only competition sessions
        const competitionOnly = sessionsData.filter((session: any) => session.trainingType === 'competition');
        setCompetitionSessions(competitionOnly);
        
        // If filterAthleteId is provided, auto-select that athlete in the form
        if (filterAthleteId) {
          setNewCompetitionSessionData(prev => ({
            ...prev,
            athleteIds: [filterAthleteId],
          }));
        }
        
        // Extract Banana Cup competitions into sessions
        const bananaCupSessionsList: BananaCupSessionRecord[] = [];
        if (Array.isArray(bananaCupData)) {
          bananaCupData.forEach((bananaCup: any) => {
            if (bananaCup.competitions && Array.isArray(bananaCup.competitions)) {
              bananaCup.competitions.forEach((competition: any) => {
                if (competition.isCompleted) {
                  bananaCupSessionsList.push({
                    _id: `${bananaCup._id}-${competition.competitionNumber}`,
                    bananaCupId: bananaCup._id,
                    bananaCupName: bananaCup.name,
                    competitionNumber: competition.competitionNumber,
                    date: competition.date || '',
                    startTime: competition.startTime || '',
                    endTime: competition.endTime || '',
                    location: competition.location || '',
                    weather: competition.weather || '',
                    hillSize: competition.hillSize || '',
                    jumps: competition.jumps || [],
                    isCompleted: competition.isCompleted,
                    athleteIds: bananaCup.athleteIds || [],
                  });
                }
              });
            }
          });
        }
        setBananaCupSessions(bananaCupSessionsList);
        setError(null);
      } catch (err) {
        setError('Failed to load data. Please try again.');
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const hasRestoredCompetitionSessionRef = useRef(false);
  useEffect(() => {
    if (competitionSessions.length === 0) return;
    if (hasRestoredCompetitionSessionRef.current) return;
    const storedSessionId = (window as any).currentSessionId;
    if (!storedSessionId) return;
    const session = competitionSessions.find((s: CompetitionSessionRecord) => s._id === storedSessionId);
    if (!session) return;
    hasRestoredCompetitionSessionRef.current = true;
    setIsCompetitionSessionActive(true);
  }, [competitionSessions]);

  // Effect to auto-populate form when athlete is selected
  useEffect(() => {
    if (formData.athleteId && competitionSessions.length > 0) {
      const lastValues = getLastUsedValuesForAthlete(formData.athleteId);
      if (lastValues) {
        const athleteSuits = getEquipmentByType('suit', formData.athleteId);
        const athleteSkis = getEquipmentByType('skis', formData.athleteId);
        const athleteShoes = getEquipmentByType('shoes', formData.athleteId);
        const athleteBindings = getEquipmentByType('bindings', formData.athleteId);
        
        // Equipment is now handled per round, so we don't need to set legacy fields here
        setFormData(prev => ({
          ...prev,
          hillSize: lastValues.hillSize || prev.hillSize,
        }));
      }
    }
  }, [competitionSessions, formData.athleteId]);

  // Effect to load coachDescription when session becomes active
  useEffect(() => {
    const currentSessionId = (window as any).currentSessionId;
    if (currentSessionId && isCompetitionSessionActive) {
      const session = competitionSessions.find(s => s._id === currentSessionId);
      if (session && session.coachDescription) {
        setCoachDescription(session.coachDescription);
      }
    } else if (!isCompetitionSessionActive) {
      // Clear coachDescription when session is not active
      setCoachDescription('');
    }
  }, [isCompetitionSessionActive, competitionSessions]);

  // Helper function to normalize decimal separators (comma to period)
  const normalizeDecimalInput = (value: string): string => {
    // Replace comma with period for decimal separator
    return value.replace(',', '.');
  };

  // Helper function to parse numeric value, handling both comma and period as decimal separator
  const parseNumericValue = (value: string): number => {
    if (!value || value.trim() === '') return 0;
    // Normalize comma to period, then parse
    const normalized = normalizeDecimalInput(value.trim());
    const parsed = Number(normalized);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Handler for decimal input fields (distance, speed) that allows comma/dot
  const handleDecimalInputChange = (fieldName: string, value: string) => {
    // Remove any non-numeric characters except comma and period
    let cleanedValue = value.replace(/[^\d,.-]/g, '');
    
    // Allow both comma and period as decimal separator, but only one
    const hasComma = cleanedValue.includes(',');
    const hasPeriod = cleanedValue.includes('.');
    if (hasComma && hasPeriod) {
      // If both exist, keep the last one
      const lastComma = cleanedValue.lastIndexOf(',');
      const lastPeriod = cleanedValue.lastIndexOf('.');
      if (lastComma > lastPeriod) {
        cleanedValue = cleanedValue.replace(/\./g, '');
      } else {
        cleanedValue = cleanedValue.replace(/,/g, '');
      }
    }
    
    // Only allow numbers and one decimal separator
    if (cleanedValue === '' || /^-?\d*[,.]?\d*$/.test(cleanedValue)) {
      // Update raw input value
      setRawInputValues(prev => ({ ...prev, [fieldName]: cleanedValue }));
      
      // Update form data with parsed numeric value
      const normalizedValue = cleanedValue.replace(',', '.');
      const numValue = normalizedValue === '' || normalizedValue === '-' ? 0 : parseFloat(normalizedValue) || 0;
      
      setFormData(prev => ({
        ...prev,
        [fieldName]: numValue,
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle decimal inputs (distance, speed) separately
    if (name.includes('Distance') || name.includes('Speed')) {
      handleDecimalInputChange(name, value);
      return;
    }
    if (name.includes('Wind')) {
      // Allow digits, minus, comma and period; normalize comma to dot
      const normalized = value.replace(/,/g, '.').replace(/[^\d.-]/g, '');
      const withOneMinus = normalized.startsWith('-') ? '-' + normalized.slice(1).replace(/-/g, '') : normalized.replace(/-/g, '');
      const parts = withOneMinus.split('.');
      const withOneDot = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : withOneMinus;
      setFormData(prev => ({ ...prev, [name]: withOneDot }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('Gate') || name === 'result' || name.includes('Rating')
        ? parseNumericValue(value)
        : value,
    }));
  };

  // Function to get the last used values for an athlete
  const getLastUsedValuesForAthlete = (athleteId: string) => {
    // Find all competition jumps for this athlete
    const allCompetitionJumps = competitionSessions.flatMap(session => 
      (session.competitionJumps || []).map(jump => ({
        ...jump,
        sessionDate: session.date,
        sessionCreatedAt: session.createdAt,
      }))
    );
    
    const athleteJumps = allCompetitionJumps.filter(jump => {
      const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
        ? (jump.athleteId as PopulatedAthlete)._id 
        : jump.athleteId as string;
      return jumpAthleteId === athleteId;
    });
    
    if (athleteJumps.length === 0) {
      return null;
    }
    
    // Sort by session date and creation time to get the most recent
    const sortedJumps = athleteJumps.sort((a, b) => {
      const dateComparison = new Date(b.sessionDate).getTime() - new Date(a.sessionDate).getTime();
      if (dateComparison !== 0) return dateComparison;
      return new Date(b.sessionCreatedAt).getTime() - new Date(a.sessionCreatedAt).getTime();
    });
    
    const lastJump = sortedJumps[0];
    return {
      // Use round-specific equipment if available, otherwise fall back to legacy fields
      trialRoundSuitId: lastJump.trialRoundSuitId || (lastJump as any).suitId || '',
      trialRoundSkisId: lastJump.trialRoundSkisId || (lastJump as any).skisId || '',
      trialRoundShoesId: lastJump.trialRoundShoesId || (lastJump as any).shoesId || '',
      trialRoundBindingsId: lastJump.trialRoundBindingsId || (lastJump as any).bindingsId || '',
      firstRoundSuitId: lastJump.firstRoundSuitId || (lastJump as any).suitId || '',
      firstRoundSkisId: lastJump.firstRoundSkisId || (lastJump as any).skisId || '',
      firstRoundShoesId: lastJump.firstRoundShoesId || (lastJump as any).shoesId || '',
      firstRoundBindingsId: lastJump.firstRoundBindingsId || (lastJump as any).bindingsId || '',
      secondRoundSuitId: lastJump.secondRoundSuitId || (lastJump as any).suitId || '',
      secondRoundSkisId: lastJump.secondRoundSkisId || (lastJump as any).skisId || '',
      secondRoundShoesId: lastJump.secondRoundShoesId || (lastJump as any).shoesId || '',
      secondRoundBindingsId: lastJump.secondRoundBindingsId || (lastJump as any).bindingsId || '',
      hillSize: lastJump.hillSize || '',
      trialRoundStartingGate: lastJump.trialRoundStartingGate || 0,
      trialRoundDistance: lastJump.trialRoundDistance || 0,
      trialRoundWind: lastJump.trialRoundWind || '',
      trialRoundSpeed: lastJump.trialRoundSpeed || 0,
      trialRoundTelemark: lastJump.trialRoundTelemark || false,
      firstRoundStartingGate: lastJump.firstRoundStartingGate || 0,
      firstRoundDistance: lastJump.firstRoundDistance || 0,
      firstRoundWind: lastJump.firstRoundWind || '',
      firstRoundSpeed: lastJump.firstRoundSpeed || 0,
      firstRoundTelemark: lastJump.firstRoundTelemark || false,
      secondRoundStartingGate: lastJump.secondRoundStartingGate || 0,
      secondRoundDistance: lastJump.secondRoundDistance || 0,
      secondRoundWind: lastJump.secondRoundWind || '',
      secondRoundSpeed: lastJump.secondRoundSpeed || 0,
      secondRoundTelemark: lastJump.secondRoundTelemark || false,
    };
  };

  // Function to handle athlete selection in jump form
  const handleAthleteSelectionInJumpForm = (athleteId: string) => {
    const lastValues = getLastUsedValuesForAthlete(athleteId);
    
    const athleteSuits = getEquipmentByType('suit', athleteId);
    const athleteSkis = getEquipmentByType('skis', athleteId);
    const athleteShoes = getEquipmentByType('shoes', athleteId);
    const athleteBindings = getEquipmentByType('bindings', athleteId);
    
    const populatedFields = new Set<string>();
    
    if (lastValues) {
      // Trial round equipment
      if (lastValues.trialRoundSuitId && athleteSuits.some(suit => suit._id === lastValues.trialRoundSuitId)) populatedFields.add('trialRoundSuitId');
      if (lastValues.trialRoundSkisId && athleteSkis.some(skis => skis._id === lastValues.trialRoundSkisId)) populatedFields.add('trialRoundSkisId');
      if (lastValues.trialRoundShoesId && athleteShoes.some(shoes => shoes._id === lastValues.trialRoundShoesId)) populatedFields.add('trialRoundShoesId');
      if (lastValues.trialRoundBindingsId && athleteBindings.some(bindings => bindings._id === lastValues.trialRoundBindingsId)) populatedFields.add('trialRoundBindingsId');
      // First round equipment
      if (lastValues.firstRoundSuitId && athleteSuits.some(suit => suit._id === lastValues.firstRoundSuitId)) populatedFields.add('firstRoundSuitId');
      if (lastValues.firstRoundSkisId && athleteSkis.some(skis => skis._id === lastValues.firstRoundSkisId)) populatedFields.add('firstRoundSkisId');
      if (lastValues.firstRoundShoesId && athleteShoes.some(shoes => shoes._id === lastValues.firstRoundShoesId)) populatedFields.add('firstRoundShoesId');
      if (lastValues.firstRoundBindingsId && athleteBindings.some(bindings => bindings._id === lastValues.firstRoundBindingsId)) populatedFields.add('firstRoundBindingsId');
      // Second round equipment
      if (lastValues.secondRoundSuitId && athleteSuits.some(suit => suit._id === lastValues.secondRoundSuitId)) populatedFields.add('secondRoundSuitId');
      if (lastValues.secondRoundSkisId && athleteSkis.some(skis => skis._id === lastValues.secondRoundSkisId)) populatedFields.add('secondRoundSkisId');
      if (lastValues.secondRoundShoesId && athleteShoes.some(shoes => shoes._id === lastValues.secondRoundShoesId)) populatedFields.add('secondRoundShoesId');
      if (lastValues.secondRoundBindingsId && athleteBindings.some(bindings => bindings._id === lastValues.secondRoundBindingsId)) populatedFields.add('secondRoundBindingsId');
      // Trial round data
      if (lastValues.trialRoundStartingGate) populatedFields.add('trialRoundStartingGate');
      if (lastValues.trialRoundDistance) populatedFields.add('trialRoundDistance');
      if (lastValues.trialRoundWind) populatedFields.add('trialRoundWind');
      if (lastValues.trialRoundSpeed) populatedFields.add('trialRoundSpeed');
      // First round data
      if (lastValues.firstRoundStartingGate) populatedFields.add('firstRoundStartingGate');
      if (lastValues.firstRoundDistance) populatedFields.add('firstRoundDistance');
      if (lastValues.firstRoundWind) populatedFields.add('firstRoundWind');
      if (lastValues.firstRoundSpeed) populatedFields.add('firstRoundSpeed');
      // Second round data
      if (lastValues.secondRoundStartingGate) populatedFields.add('secondRoundStartingGate');
      if (lastValues.secondRoundDistance) populatedFields.add('secondRoundDistance');
      if (lastValues.secondRoundWind) populatedFields.add('secondRoundWind');
      if (lastValues.secondRoundSpeed) populatedFields.add('secondRoundSpeed');
    }
    
    setAutoPopulatedFields(populatedFields);
    
    setFormData(prev => ({
      ...prev,
      athleteId,
      hillSize: lastValues ? lastValues.hillSize : '',
      // Trial round
      trialRoundSuitId: lastValues && lastValues.trialRoundSuitId && athleteSuits.some(suit => suit._id === lastValues.trialRoundSuitId) ? lastValues.trialRoundSuitId : '',
      trialRoundSkisId: lastValues && lastValues.trialRoundSkisId && athleteSkis.some(skis => skis._id === lastValues.trialRoundSkisId) ? lastValues.trialRoundSkisId : '',
      trialRoundShoesId: lastValues && lastValues.trialRoundShoesId && athleteShoes.some(shoes => shoes._id === lastValues.trialRoundShoesId) ? lastValues.trialRoundShoesId : '',
      trialRoundBindingsId: lastValues && lastValues.trialRoundBindingsId && athleteBindings.some(bindings => bindings._id === lastValues.trialRoundBindingsId) ? lastValues.trialRoundBindingsId : '',
      trialRoundStartingGate: lastValues ? lastValues.trialRoundStartingGate : 0,
      trialRoundDistance: lastValues ? lastValues.trialRoundDistance : 0,
      trialRoundWind: lastValues ? lastValues.trialRoundWind : '',
      trialRoundSpeed: lastValues ? lastValues.trialRoundSpeed : 0,
      trialRoundTelemark: lastValues ? lastValues.trialRoundTelemark : false,
      // First round
      firstRoundSuitId: lastValues && lastValues.firstRoundSuitId && athleteSuits.some(suit => suit._id === lastValues.firstRoundSuitId) ? lastValues.firstRoundSuitId : '',
      firstRoundSkisId: lastValues && lastValues.firstRoundSkisId && athleteSkis.some(skis => skis._id === lastValues.firstRoundSkisId) ? lastValues.firstRoundSkisId : '',
      firstRoundShoesId: lastValues && lastValues.firstRoundShoesId && athleteShoes.some(shoes => shoes._id === lastValues.firstRoundShoesId) ? lastValues.firstRoundShoesId : '',
      firstRoundBindingsId: lastValues && lastValues.firstRoundBindingsId && athleteBindings.some(bindings => bindings._id === lastValues.firstRoundBindingsId) ? lastValues.firstRoundBindingsId : '',
      firstRoundStartingGate: lastValues ? lastValues.firstRoundStartingGate : 0,
      firstRoundDistance: lastValues ? lastValues.firstRoundDistance : 0,
      firstRoundWind: lastValues ? lastValues.firstRoundWind : '',
      firstRoundSpeed: lastValues ? lastValues.firstRoundSpeed : 0,
      firstRoundTelemark: lastValues ? lastValues.firstRoundTelemark : false,
      // Second round
      secondRoundSuitId: lastValues && lastValues.secondRoundSuitId && athleteSuits.some(suit => suit._id === lastValues.secondRoundSuitId) ? lastValues.secondRoundSuitId : '',
      secondRoundSkisId: lastValues && lastValues.secondRoundSkisId && athleteSkis.some(skis => skis._id === lastValues.secondRoundSkisId) ? lastValues.secondRoundSkisId : '',
      secondRoundShoesId: lastValues && lastValues.secondRoundShoesId && athleteShoes.some(shoes => shoes._id === lastValues.secondRoundShoesId) ? lastValues.secondRoundShoesId : '',
      secondRoundBindingsId: lastValues && lastValues.secondRoundBindingsId && athleteBindings.some(bindings => bindings._id === lastValues.secondRoundBindingsId) ? lastValues.secondRoundBindingsId : '',
      secondRoundStartingGate: lastValues ? lastValues.secondRoundStartingGate : 0,
      secondRoundDistance: lastValues ? lastValues.secondRoundDistance : 0,
      secondRoundWind: lastValues ? lastValues.secondRoundWind : '',
      secondRoundSpeed: lastValues ? lastValues.secondRoundSpeed : 0,
      secondRoundTelemark: lastValues ? lastValues.secondRoundTelemark : false,
    }));
    
    setTimeout(() => {
      setAutoPopulatedFields(new Set());
    }, 3000);
  };

  const handleTrialRoundTelemarkToggle = (value: boolean) => {
    setFormData(prev => ({ ...prev, trialRoundTelemark: value }));
  };

  const handleFirstRoundTelemarkToggle = (value: boolean) => {
    setFormData(prev => ({ ...prev, firstRoundTelemark: value }));
  };

  const handleSecondRoundTelemarkToggle = (value: boolean) => {
    setFormData(prev => ({ ...prev, secondRoundTelemark: value }));
  };

  // New competition session form handlers
  const handleNewCompetitionSessionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setNewCompetitionSessionData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Validate that end time is after start time
    if (name === 'startTime' || name === 'endTime') {
      const updatedData = { ...newCompetitionSessionData, [name]: value };
      
      if (updatedData.startTime && updatedData.endTime) {
        const startTime = new Date(`2000-01-01T${updatedData.startTime}`);
        const endTime = new Date(`2000-01-01T${updatedData.endTime}`);
        
        if (endTime <= startTime) {
          setError('invalid_end_time');
        } else {
          setError(null);
        }
      } else {
        setError(null);
      }
    }
  };

  const handleAthleteSelection = (athleteId: string) => {
    // If filterAthleteId is provided, don't allow changing athlete selection
    if (filterAthleteId) {
      return;
    }
    
    setNewCompetitionSessionData(prev => ({
      ...prev,
      athleteIds: prev.athleteIds.includes(athleteId)
        ? prev.athleteIds.filter(id => id !== athleteId)
        : [...prev.athleteIds, athleteId],
    }));
  };

  const handleNewCompetitionSessionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If editing, use update handler
    if (editingSessionId) {
      return handleUpdateSession(e);
    }
    
    if (newCompetitionSessionData.startTime && newCompetitionSessionData.endTime) {
      const startTime = new Date(`2000-01-01T${newCompetitionSessionData.startTime}`);
      const endTime = new Date(`2000-01-01T${newCompetitionSessionData.endTime}`);
      
      if (endTime <= startTime) {
        setError('invalid_end_time');
        return;
      }
    }
    
    if (newCompetitionSessionData.athleteIds.length === 0) {
      setError('no_athletes_selected');
      return;
    }
    
    try {
      const sessionData = {
        trainingType: 'competition',
        date: newCompetitionSessionData.date,
        startTime: newCompetitionSessionData.startTime,
        endTime: newCompetitionSessionData.endTime,
        location: newCompetitionSessionData.location,
        weather: newCompetitionSessionData.weather || undefined,
        category: isQualificationMode ? undefined : (newCompetitionSessionData.category || undefined),
        hillSize: newCompetitionSessionData.hillSize && newCompetitionSessionData.hillSize.trim() !== '' 
          ? newCompetitionSessionData.hillSize.trim() 
          : undefined,
        ageCategory: !isQualificationMode && (newCompetitionSessionData.category === 'national_championships' || newCompetitionSessionData.category === 'national_cup') 
          ? (newCompetitionSessionData.ageCategory || undefined) 
          : undefined,
        athleteIds: newCompetitionSessionData.athleteIds,
        jumps: [],
        isQualification: isQualificationMode
      };

      const newCompetitionSession = await trainingSessionAPI.create(sessionData);
      
      setCompetitionSessions(prev => [newCompetitionSession, ...prev]);
      
      setShowJumpForm(true);
      setIsCompetitionSessionActive(true);
      setActiveSessionIsQualification(isQualificationMode);
      setTimeout(() => editJumpFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      // Set athlete if filterAthleteId is provided
      if (filterAthleteId && newCompetitionSessionData.athleteIds.includes(filterAthleteId)) {
        setFormData(prev => ({ ...prev, athleteId: filterAthleteId, date: newCompetitionSessionData.date }));
      } else {
        setFormData(prev => ({ ...prev, date: newCompetitionSessionData.date }));
      }
      (window as any).currentSessionId = newCompetitionSession._id;
      
      setError(null);
    } catch (err) {
      console.error('Detailed error creating competition session:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to create competition session: ${errorMessage}`);
    }
  };

  const handleFinishCompetitionSession = async () => {
    try {
      const currentSessionId = (window as any).currentSessionId;
      if (currentSessionId && coachDescription.trim()) {
        await trainingSessionAPI.update(currentSessionId, {
          coachDescription: coachDescription.trim(),
          isCompleted: true
        });
      } else if (currentSessionId) {
        await trainingSessionAPI.update(currentSessionId, {
          isCompleted: true
        });
      }
      
      setIsCompetitionSessionActive(false);
      setShowJumpForm(false);
      setActiveSessionIsQualification(false);
      setIsQualificationMode(false);
      setCoachDescription('');
      setNewCompetitionSessionData({
        date: new Date().toISOString().split('T')[0],
        startTime: '',
        endTime: '',
        location: '',
        weather: '',
        category: '',
        hillSize: '',
        ageCategory: '',
        athleteIds: [],
      });
      setFormData(prev => ({
        ...prev,
        firstRoundStartingGate: 0,
        firstRoundDistance: 0,
        firstRoundWind: '',
        firstRoundSpeed: 0,
        firstRoundTelemark: false,
        secondRoundStartingGate: 0,
        secondRoundDistance: 0,
        secondRoundWind: '',
        secondRoundSpeed: 0,
        secondRoundTelemark: false,
        result: 0,
        trialRoundRating: 0,
        firstRoundRating: 0,
        secondRoundRating: 0,
      }));
      setRawInputValues({
        trialRoundDistance: '',
        trialRoundSpeed: '',
        firstRoundDistance: '',
        firstRoundSpeed: '',
        secondRoundDistance: '',
        secondRoundSpeed: '',
      });
      
      (window as any).currentSessionId = null;
      setEditingSessionId(null);
      setEditingJumpId(null);
      setEditingSessionData(null);
      
      // Refresh data
      const sessionsResponse = filterAthleteId 
        ? await trainingSessionAPI.getByAthlete(filterAthleteId, undefined, undefined, true) // Load all
        : await trainingSessionAPI.getAll(undefined, undefined, true); // Load all
      // Handle response - when all=true, returns array directly, otherwise paginated object
      const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);
      const competitionSessionsData = sessionsData.filter((s: any) => 
        s.trainingType === 'competition' && s.isDeleted !== true
      );
      setCompetitionSessions(competitionSessionsData.filter((s: CompetitionSessionRecord) => {
        // Show sessions that are explicitly marked as completed OR have competition jumps (for backward compatibility)
        return s.isCompleted === true || (s.competitionJumps && s.competitionJumps.length > 0);
      }));
      
      setError(null);
    } catch (err) {
      setError('Failed to finish competition session. Please try again.');
      console.error('Error finishing competition session:', err);
    }
  };

  const handlePauseCompetitionSession = async (sessionId: string) => {
    try {
      const updatedSession = await trainingSessionAPI.pause(sessionId);
      setCompetitionSessions(prev => prev.map(session =>
        session._id === sessionId ? updatedSession : session
      ));
    } catch (err) {
      setError('Failed to pause competition session. Please try again.');
      console.error('Error pausing competition session:', err);
    }
  };

  const handleContinueCompetitionSession = async (sessionId: string) => {
    try {
      const updatedSession = await trainingSessionAPI.resume(sessionId);
      setCompetitionSessions(prev => prev.map(session =>
        session._id === sessionId ? updatedSession : session
      ));
      // Load coachDescription if it exists
      if (updatedSession.coachDescription) {
        setCoachDescription(updatedSession.coachDescription);
      }
    } catch (err) {
      setError('Failed to continue competition session. Please try again.');
      console.error('Error continuing competition session:', err);
    }
  };

  const handleDeleteCompetitionSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to delete this competition session?')) {
      return;
    }
    try {
      await trainingSessionAPI.delete(sessionId);
      setCompetitionSessions(prev => prev.filter(session => session._id !== sessionId));
    } catch (err) {
      setError('Failed to delete competition session. Please try again.');
      console.error('Error deleting competition session:', err);
    }
  };

  // Edit session handler
  const handleEditSession = (session: CompetitionSessionRecord) => {
    setEditingSessionId(session._id);
    setEditingSessionData({
      date: session.date,
      startTime: session.startTime,
      endTime: session.endTime,
      location: session.location,
      weather: session.weather,
      category: session.category || '',
      hillSize: session.hillSize || '',
      ageCategory: session.ageCategory || '',
      athleteIds: session.athleteIds,
    });
    setIsQualificationMode(!!session.isQualification);
    setShowNewCompetitionSessionForm(true);
    // Scroll to form after a short delay to ensure it's rendered
    setTimeout(() => {
      editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Cancel edit handler
  const handleCancelEdit = () => {
    setEditingSessionId(null);
    setEditingJumpId(null);
    setEditingSessionData(null);
    setShowNewCompetitionSessionForm(false);
    setShowJumpForm(false);
    setIsQualificationMode(false);
    setActiveSessionIsQualification(false);
    setNewCompetitionSessionData({
      date: new Date().toISOString().split('T')[0],
      startTime: '',
      endTime: '',
      location: '',
      weather: '',
      category: '',
      hillSize: '',
      ageCategory: '',
      athleteIds: [],
    });
        setFormData(prev => ({
          ...prev,
          trialRoundStartingGate: 0,
          trialRoundDistance: 0,
          trialRoundWind: '',
          trialRoundSpeed: 0,
          trialRoundTelemark: false,
          firstRoundStartingGate: 0,
          firstRoundDistance: 0,
          firstRoundWind: '',
          firstRoundSpeed: 0,
          firstRoundTelemark: false,
          secondRoundStartingGate: 0,
          secondRoundDistance: 0,
          secondRoundWind: '',
          secondRoundSpeed: 0,
          secondRoundTelemark: false,
          result: 0,
        }));
        setRawInputValues({
          trialRoundDistance: '',
          trialRoundSpeed: '',
          firstRoundDistance: '',
          firstRoundSpeed: '',
          secondRoundDistance: '',
          secondRoundSpeed: '',
        });
  };

  // Update session handler
  const handleUpdateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingSessionId || !editingSessionData) return;
    
    if (editingSessionData.startTime && editingSessionData.endTime) {
      const startTime = new Date(`2000-01-01T${editingSessionData.startTime}`);
      const endTime = new Date(`2000-01-01T${editingSessionData.endTime}`);
      
      if (endTime <= startTime) {
        setError('invalid_end_time');
        return;
      }
    }
    
    if (editingSessionData.athleteIds.length === 0) {
      setError('no_athletes_selected');
      return;
    }
    
    try {
      const sessionData = {
        date: editingSessionData.date,
        startTime: editingSessionData.startTime,
        endTime: editingSessionData.endTime,
        location: editingSessionData.location,
        weather: editingSessionData.weather || undefined,
        category: editingSessionData.category || undefined,
        hillSize: editingSessionData.hillSize && editingSessionData.hillSize.trim() !== '' 
          ? editingSessionData.hillSize.trim() 
          : undefined,
        ageCategory: (editingSessionData.category === 'national_championships' || editingSessionData.category === 'national_cup') ? (editingSessionData.ageCategory || undefined) : undefined,
        athleteIds: editingSessionData.athleteIds,
        isQualification: isQualificationMode
      };

      const updatedSession = await trainingSessionAPI.update(editingSessionId, sessionData);
      
      setCompetitionSessions(prev => prev.map(session => 
        session._id === editingSessionId ? updatedSession : session
      ));
      
      setEditingSessionId(null);
      setEditingSessionData(null);
      setShowNewCompetitionSessionForm(false);
      setError(null);
    } catch (err) {
      console.error('Error updating competition session:', err);
      setError('Failed to update competition session. Please try again.');
    }
  };

  // Helper function to extract equipment ID from object or string
  const getEquipmentId = (equipment: string | any): string => {
    if (!equipment) return '';
    if (typeof equipment === 'string') return equipment;
    if (typeof equipment === 'object' && equipment !== null && '_id' in equipment) {
      return (equipment as any)._id;
    }
    return '';
  };

  // Edit jump handler
  const handleEditJump = (sessionId: string, jump: CompetitionJumpRecord) => {
    setEditingJumpId(jump._id);
    (window as any).currentSessionId = sessionId;
    const session = competitionSessions.find(s => s._id === sessionId);
    setActiveSessionIsQualification(!!session?.isQualification);
    const athleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
      ? (jump.athleteId as any)._id 
      : jump.athleteId;
    
    // Extract equipment per round, fall back to legacy fields if round-specific not available
    const trialRoundSuitId = jump.trialRoundSuitId || jump.suitId;
    const trialRoundSkisId = jump.trialRoundSkisId || jump.skisId;
    const trialRoundShoesId = jump.trialRoundShoesId || jump.shoesId;
    const trialRoundBindingsId = jump.trialRoundBindingsId || jump.bindingsId;
    const firstRoundSuitId = jump.firstRoundSuitId || jump.suitId;
    const firstRoundSkisId = jump.firstRoundSkisId || jump.skisId;
    const firstRoundShoesId = jump.firstRoundShoesId || jump.shoesId;
    const firstRoundBindingsId = jump.firstRoundBindingsId || jump.bindingsId;
    const secondRoundSuitId = jump.secondRoundSuitId || jump.suitId;
    const secondRoundSkisId = jump.secondRoundSkisId || jump.skisId;
    const secondRoundShoesId = jump.secondRoundShoesId || jump.shoesId;
    const secondRoundBindingsId = jump.secondRoundBindingsId || jump.bindingsId;
    
    setFormData({
      athleteId: athleteId,
      hillSize: jump.hillSize || '',
      // Trial round
      trialRoundSuitId: getEquipmentId(trialRoundSuitId),
      trialRoundSkisId: getEquipmentId(trialRoundSkisId),
      trialRoundShoesId: getEquipmentId(trialRoundShoesId),
      trialRoundBindingsId: getEquipmentId(trialRoundBindingsId),
      trialRoundStartingGate: jump.trialRoundStartingGate || 0,
      trialRoundDistance: jump.trialRoundDistance || 0,
      trialRoundWind: jump.trialRoundWind || '',
      trialRoundSpeed: jump.trialRoundSpeed || 0,
      trialRoundTelemark: jump.trialRoundTelemark || false,
      // First round
      firstRoundSuitId: getEquipmentId(firstRoundSuitId),
      firstRoundSkisId: getEquipmentId(firstRoundSkisId),
      firstRoundShoesId: getEquipmentId(firstRoundShoesId),
      firstRoundBindingsId: getEquipmentId(firstRoundBindingsId),
      firstRoundStartingGate: jump.firstRoundStartingGate || 0,
      firstRoundDistance: jump.firstRoundDistance || 0,
      firstRoundWind: jump.firstRoundWind || '',
      firstRoundSpeed: jump.firstRoundSpeed || 0,
      firstRoundTelemark: jump.firstRoundTelemark || false,
      // Second round
      secondRoundSuitId: getEquipmentId(secondRoundSuitId),
      secondRoundSkisId: getEquipmentId(secondRoundSkisId),
      secondRoundShoesId: getEquipmentId(secondRoundShoesId),
      secondRoundBindingsId: getEquipmentId(secondRoundBindingsId),
      secondRoundStartingGate: jump.secondRoundStartingGate || 0,
      secondRoundDistance: jump.secondRoundDistance || 0,
      secondRoundWind: jump.secondRoundWind || '',
      secondRoundSpeed: jump.secondRoundSpeed || 0,
      secondRoundTelemark: jump.secondRoundTelemark || false,
      result: jump.result || 0,
      trialRoundRating: (jump as any).trialRoundRating ?? 0,
      firstRoundRating: (jump as any).firstRoundRating ?? jump.rating ?? 0,
      secondRoundRating: (jump as any).secondRoundRating ?? 0,
    });
    setRawInputValues({
      trialRoundDistance: jump.trialRoundDistance ? String(jump.trialRoundDistance) : '',
      trialRoundSpeed: jump.trialRoundSpeed ? String(jump.trialRoundSpeed) : '',
      firstRoundDistance: jump.firstRoundDistance ? String(jump.firstRoundDistance) : '',
      firstRoundSpeed: jump.firstRoundSpeed ? String(jump.firstRoundSpeed) : '',
      secondRoundDistance: jump.secondRoundDistance ? String(jump.secondRoundDistance) : '',
      secondRoundSpeed: jump.secondRoundSpeed ? String(jump.secondRoundSpeed) : '',
    });
    setShowJumpForm(true);
    // Scroll to form after a short delay to ensure it's rendered
    setTimeout(() => {
      editJumpFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Update rating for a jump directly (per round)
  const handleUpdateJumpRating = async (
    sessionId: string,
    jumpId: string,
    round: 'trial' | 'first' | 'second',
    newRating: number
  ) => {
    try {
      const session = competitionSessions.find(s => s._id === sessionId);
      if (!session) return;

      const updatedJumps = session.competitionJumps.map(jump => {
        if (jump._id !== jumpId) return jump;
        const updated = { ...jump } as any;
        if (round === 'trial') updated.trialRoundRating = newRating || undefined;
        else if (round === 'first') {
          updated.firstRoundRating = newRating || undefined;
          updated.rating = newRating || undefined;
        } else updated.secondRoundRating = newRating || undefined;
        return updated;
      });

      const updatedSession = await trainingSessionAPI.update(sessionId, {
        competitionJumps: updatedJumps,
      });

      setCompetitionSessions(prev =>
        prev.map(s => (s._id === sessionId ? updatedSession : s))
      );
    } catch (err) {
      console.error('Error updating jump rating:', err);
      setError('Failed to update rating. Please try again.');
    }
  };

  // Update jump handler
  const handleUpdateJump = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingJumpId) return;
    
    const currentSessionId = (window as any).currentSessionId;
    if (!currentSessionId) return;
    
    try {
      const session = competitionSessions.find(s => s._id === currentSessionId);
      if (!session) return;
      
      // Get hillSize from the session
      const hillSize = session.hillSize || '';
      
      const updatedJumps = session.competitionJumps.map(jump => {
        if (jump._id === editingJumpId) {
          return {
            ...jump,
            athleteId: formData.athleteId,
            hillSize: hillSize,
            // Trial round data and equipment
            trialRoundStartingGate: formData.trialRoundStartingGate || undefined,
            trialRoundDistance: formData.trialRoundDistance || undefined,
            trialRoundWind: formData.trialRoundWind || undefined,
            trialRoundSpeed: formData.trialRoundSpeed || undefined,
            trialRoundTelemark: formData.trialRoundTelemark || undefined,
            trialRoundSuitId: formData.trialRoundSuitId || undefined,
            trialRoundSkisId: formData.trialRoundSkisId || undefined,
            trialRoundShoesId: formData.trialRoundShoesId || undefined,
            trialRoundBindingsId: formData.trialRoundBindingsId || undefined,
            // First round data and equipment
            firstRoundStartingGate: formData.firstRoundStartingGate,
            firstRoundDistance: formData.firstRoundDistance,
            firstRoundWind: formData.firstRoundWind,
            firstRoundSpeed: formData.firstRoundSpeed,
            firstRoundTelemark: formData.firstRoundTelemark,
            firstRoundSuitId: formData.firstRoundSuitId || undefined,
            firstRoundSkisId: formData.firstRoundSkisId || undefined,
            firstRoundShoesId: formData.firstRoundShoesId || undefined,
            firstRoundBindingsId: formData.firstRoundBindingsId || undefined,
            // Second round data and equipment
            secondRoundStartingGate: formData.secondRoundStartingGate,
            secondRoundDistance: formData.secondRoundDistance,
            secondRoundWind: formData.secondRoundWind,
            secondRoundSpeed: formData.secondRoundSpeed,
            secondRoundTelemark: formData.secondRoundTelemark,
            secondRoundSuitId: formData.secondRoundSuitId || undefined,
            secondRoundSkisId: formData.secondRoundSkisId || undefined,
            secondRoundShoesId: formData.secondRoundShoesId || undefined,
            secondRoundBindingsId: formData.secondRoundBindingsId || undefined,
            result: formData.result,
            trialRoundRating: formData.trialRoundRating || undefined,
            firstRoundRating: formData.firstRoundRating || undefined,
            secondRoundRating: formData.secondRoundRating || undefined,
            rating: formData.firstRoundRating || undefined,
          };
        }
        return jump;
      });
      
      const updatedSession = await trainingSessionAPI.update(currentSessionId, {
        competitionJumps: updatedJumps,
      });
      
      setCompetitionSessions(prev => prev.map(session => 
        session._id === currentSessionId ? updatedSession : session
      ));
      
      setEditingJumpId(null);
      setFormData(prev => ({
        ...prev,
        trialRoundStartingGate: 0,
        trialRoundDistance: 0,
        trialRoundWind: '',
        trialRoundSpeed: 0,
        trialRoundTelemark: false,
        trialRoundSuitId: '',
        trialRoundSkisId: '',
        trialRoundShoesId: '',
        trialRoundBindingsId: '',
        firstRoundStartingGate: 0,
        firstRoundDistance: 0,
        firstRoundWind: '',
        firstRoundSpeed: 0,
        firstRoundTelemark: false,
        firstRoundSuitId: '',
        firstRoundSkisId: '',
        firstRoundShoesId: '',
        firstRoundBindingsId: '',
        secondRoundStartingGate: 0,
        secondRoundDistance: 0,
        secondRoundWind: '',
        secondRoundSpeed: 0,
        secondRoundTelemark: false,
        secondRoundSuitId: '',
        secondRoundSkisId: '',
        secondRoundShoesId: '',
        secondRoundBindingsId: '',
        result: 0,
        trialRoundRating: 0,
        firstRoundRating: 0,
        secondRoundRating: 0,
      }));
      setError(null);
    } catch (err) {
      setError('Failed to update jump. Please try again.');
      console.error('Error updating jump:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If editing, use update handler
    if (editingJumpId) {
      return handleUpdateJump(e);
    }
    
    try {
      const currentSessionId = (window as any).currentSessionId;
      
      if (currentSessionId) {
        // Get hillSize from the session
        const session = competitionSessions.find(s => s._id === currentSessionId);
        const hillSize = session?.hillSize || '';
        const isQualificationSession = session?.isQualification || currentSessionIsQualification;
        
        const competitionJumpData: any = {
          athleteId: formData.athleteId,
          hillSize: hillSize,
          // First round data and equipment
          firstRoundStartingGate: formData.firstRoundStartingGate,
          firstRoundDistance: formData.firstRoundDistance,
          firstRoundWind: formData.firstRoundWind,
          firstRoundSpeed: formData.firstRoundSpeed,
          firstRoundTelemark: formData.firstRoundTelemark,
          firstRoundSuitId: formData.firstRoundSuitId || undefined,
          firstRoundSkisId: formData.firstRoundSkisId || undefined,
          firstRoundShoesId: formData.firstRoundShoesId || undefined,
          firstRoundBindingsId: formData.firstRoundBindingsId || undefined,
          result: formData.result,
        };
        
        if (!isQualificationSession) {
          competitionJumpData.trialRoundStartingGate = formData.trialRoundStartingGate || undefined;
          competitionJumpData.trialRoundDistance = formData.trialRoundDistance || undefined;
          competitionJumpData.trialRoundWind = formData.trialRoundWind || undefined;
          competitionJumpData.trialRoundSpeed = formData.trialRoundSpeed || undefined;
          competitionJumpData.trialRoundTelemark = formData.trialRoundTelemark || undefined;
          competitionJumpData.trialRoundSuitId = formData.trialRoundSuitId || undefined;
          competitionJumpData.trialRoundSkisId = formData.trialRoundSkisId || undefined;
          competitionJumpData.trialRoundShoesId = formData.trialRoundShoesId || undefined;
          competitionJumpData.trialRoundBindingsId = formData.trialRoundBindingsId || undefined;
          
          competitionJumpData.secondRoundStartingGate = formData.secondRoundStartingGate || undefined;
          competitionJumpData.secondRoundDistance = formData.secondRoundDistance || undefined;
          competitionJumpData.secondRoundWind = formData.secondRoundWind || undefined;
          competitionJumpData.secondRoundSpeed = formData.secondRoundSpeed || undefined;
          competitionJumpData.secondRoundTelemark = formData.secondRoundTelemark || undefined;
          competitionJumpData.secondRoundSuitId = formData.secondRoundSuitId || undefined;
          competitionJumpData.secondRoundSkisId = formData.secondRoundSkisId || undefined;
          competitionJumpData.secondRoundShoesId = formData.secondRoundShoesId || undefined;
          competitionJumpData.secondRoundBindingsId = formData.secondRoundBindingsId || undefined;
        }
        
        competitionJumpData.trialRoundRating = formData.trialRoundRating || undefined;
        competitionJumpData.firstRoundRating = formData.firstRoundRating || undefined;
        competitionJumpData.secondRoundRating = formData.secondRoundRating || undefined;
        competitionJumpData.rating = formData.firstRoundRating || undefined;
        
        const updatedSession = await trainingSessionAPI.addCompetitionJump(currentSessionId, competitionJumpData);
        
        if (updatedSession) {
          setCompetitionSessions(prev => prev.map(session => 
            session._id === currentSessionId ? updatedSession : session
          ));
        }
        
        setFormData(prev => ({
          ...prev,
          trialRoundStartingGate: 0,
          trialRoundDistance: 0,
          trialRoundWind: '',
          trialRoundSpeed: 0,
          trialRoundTelemark: false,
          firstRoundStartingGate: 0,
          firstRoundDistance: 0,
          firstRoundWind: '',
          firstRoundSpeed: 0,
          firstRoundTelemark: false,
          secondRoundStartingGate: 0,
          secondRoundDistance: 0,
          secondRoundWind: '',
          secondRoundSpeed: 0,
          secondRoundTelemark: false,
          result: 0,
          trialRoundRating: 0,
          firstRoundRating: 0,
          secondRoundRating: 0,
        }));
        
        // Show success notification
        setNotification({ message: 'Jump successfully added!', type: 'success' });
        setTimeout(() => setNotification(null), 3000);
        
        setError(null);
      }
    } catch (err) {
      setError('Failed to save competition session. Please try again.');
      console.error('Error saving competition session:', err);
    }
  };

  const getAthleteName = (athleteId: string | PopulatedAthlete) => {
    if (typeof athleteId === 'object' && athleteId !== null) {
      return `${athleteId.firstName} ${athleteId.lastName}`;
    }
    const athlete = athletes.find(a => a._id === athleteId);
    return athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unknown';
  };

  const getEquipmentName = (equipmentId: string | PopulatedEquipment) => {
    if (typeof equipmentId === 'object' && equipmentId !== null) {
      return equipmentId.name;
    }
    const eq = equipment.find(e => e._id === equipmentId);
    return eq ? eq.name : 'Unknown';
  };

  const getEquipmentByType = (type: 'suit' | 'skis' | 'shoes' | 'bindings', athleteId?: string) => {
    if (athleteId) {
      return equipment.filter(e => {
        const equipmentAthleteId = typeof e.athleteId === 'object' ? e.athleteId._id : e.athleteId;
        return e.type === type && equipmentAthleteId === athleteId;
      });
    }
    return equipment.filter(e => e.type === type);
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedSessions = () => {
    const completedSessions = competitionSessions.filter(session => {
      // Show sessions that are explicitly marked as completed OR have competition jumps (for backward compatibility)
      return session.isCompleted === true || (session.competitionJumps && session.competitionJumps.length > 0);
    });
    return [...completedSessions].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'startTime':
          comparison = a.startTime.localeCompare(b.startTime);
          break;
        case 'endTime':
          comparison = a.endTime.localeCompare(b.endTime);
          break;
        case 'location':
          comparison = a.location.localeCompare(b.location);
          break;
        case 'weather':
          comparison = a.weather.localeCompare(b.weather);
          break;
        case 'category':
          comparison = (a.category || '').localeCompare(b.category || '');
          break;
        case 'athlete':
          const aAthlete = a.athleteIds.length > 0 ? getAthleteName(a.athleteIds[0]) : '';
          const bAthlete = b.athleteIds.length > 0 ? getAthleteName(b.athleteIds[0]) : '';
          comparison = aAthlete.localeCompare(bAthlete);
          break;
        case 'distance':
          if (filterAthleteId) {
            // Count only competition jumps for the specific athlete
            const aAthleteJumps = (a.competitionJumps || []).filter(jump => {
              const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                ? (jump.athleteId as any)._id 
                : jump.athleteId as string;
              return jumpAthleteId === filterAthleteId;
            });
            const bAthleteJumps = (b.competitionJumps || []).filter(jump => {
              const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                ? (jump.athleteId as any)._id 
                : jump.athleteId as string;
              return jumpAthleteId === filterAthleteId;
            });
            comparison = (aAthleteJumps.length * 2) - (bAthleteJumps.length * 2);
          } else {
            const aTotalDistance = (a.competitionJumps?.length || 0) * 2;
            const bTotalDistance = (b.competitionJumps?.length || 0) * 2;
            comparison = aTotalDistance - bTotalDistance;
          }
          break;
        case 'avgDistance': {
          const aDistances: number[] = [];
          (a.competitionJumps || []).forEach(jump => {
            if (jump.firstRoundDistance && jump.firstRoundDistance > 0) aDistances.push(jump.firstRoundDistance);
            if (jump.secondRoundDistance && jump.secondRoundDistance > 0) aDistances.push(jump.secondRoundDistance);
          });
          const bDistances: number[] = [];
          (b.competitionJumps || []).forEach(jump => {
            if (jump.firstRoundDistance && jump.firstRoundDistance > 0) bDistances.push(jump.firstRoundDistance);
            if (jump.secondRoundDistance && jump.secondRoundDistance > 0) bDistances.push(jump.secondRoundDistance);
          });
          const aAvg = aDistances.length > 0 ? aDistances.reduce((sum, d) => sum + d, 0) / aDistances.length : 0;
          const bAvg = bDistances.length > 0 ? bDistances.reduce((sum, d) => sum + d, 0) / bDistances.length : 0;
          comparison = aAvg - bAvg;
          break;
        }
        case 'hillSize':
          const aHillSize = a.hillSize || '';
          const bHillSize = b.hillSize || '';
          comparison = aHillSize.localeCompare(bHillSize);
          break;
        case 'trialRound':
          const aTrialRoundCount = a.competitionJumps?.filter(jump => 
            jump.trialRoundStartingGate || jump.trialRoundDistance || jump.trialRoundWind || jump.trialRoundSpeed !== undefined
          ).length || 0;
          const bTrialRoundCount = b.competitionJumps?.filter(jump => 
            jump.trialRoundStartingGate || jump.trialRoundDistance || jump.trialRoundWind || jump.trialRoundSpeed !== undefined
          ).length || 0;
          comparison = aTrialRoundCount - bTrialRoundCount;
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Group sessions by category
  const getSessionsByCategory = (category: string) => {
    if (category === 'world_cup') {
      // Include both World Cup competitions and Qualifications in the World Cup list
      return getSortedSessions().filter(session => 
        session.category === category || session.isQualification === true
      );
    }
    // Backward compatibility: also include old 'national' category when filtering for 'national_cup'
    if (category === 'national_cup') {
      return getSortedSessions().filter(session => 
        session.category === category || session.category === 'national'
      );
    }
    return getSortedSessions().filter(session => session.category === category);
  };

  // Get category display name
  const getCategoryDisplayName = (category: string) => {
    // Backward compatibility: map old 'national' to 'national_cup'
    const normalizedCategory = category === 'national' ? 'national_cup' : category;
    const categoryMap: { [key: string]: string } = {
      'olympic_games': 'OLYMPIC GAMES',
      'world_championships': 'WORLD CHAMPIONSHIPS',
      'world_cup': 'WORLD CUP',
      'summer_grand_prix': 'SUMMER GRAND PRIX',
      'continental_cup': 'CONTINENTAL CUP',
      'fis_cup': 'FIS CUP',
      'alpen_cup': 'ALPEN CUP',
      'national_championships': 'NATIONAL CHAMPIONSHIPS',
      'national_cup': 'NATIONAL CUP',
      'national': 'NATIONAL CUP', // Backward compatibility
      'junior_world_championships': 'JUNIOR WORLD CHAMPIONSHIPS',
      'youth_olympic_games': 'YOUTH OLYMPIC GAMES',
      'eyof': 'EYOF',
      'opa_games': 'OPA GAMES',
    };
    return categoryMap[normalizedCategory] || category.toUpperCase();
  };

  // Get all categories that have at least one session
  const getCategoriesWithSessions = () => {
    // Order: Olympic Games, World Championships, World Cup, Summer Grand Prix, Continental Cup, FIS Cup, Alpen Cup, National Championships, National Cup, Junior World Championships, Youth Olympic Games, EYOF, OPA Games
    const categories = ['olympic_games', 'world_championships', 'world_cup', 'summer_grand_prix', 'continental_cup', 'fis_cup', 'alpen_cup', 'national_championships', 'national_cup', 'junior_world_championships', 'youth_olympic_games', 'eyof', 'opa_games'];
    return categories.filter(category => {
      return competitionSessions.some(session => {
        const isCompleted = session.isCompleted === true || (session.competitionJumps && session.competitionJumps.length > 0);
        // For world_cup, also include qualifications
        if (category === 'world_cup') {
          return (session.category === category || session.isQualification === true) && isCompleted;
        }
        // Backward compatibility: also check for old 'national' category
        if (category === 'national_cup') {
          return (session.category === category || session.category === 'national') && isCompleted;
        }
        return session.category === category && isCompleted;
      });
    });
  };

  const handleBananaCupSort = (field: BananaCupSortField) => {
    if (field === bananaCupSortField) {
      setBananaCupSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setBananaCupSortField(field);
      setBananaCupSortDirection('asc');
    }
  };

  // Helper function to count total rounds (jumps) in a Banana Cup session
  // Each jump record can have up to 5 rounds (firstRoundDistance, secondRoundDistance, etc.)
  // We need to count the actual rounds, not just the number of jump records
  const countBananaCupJumps = (session: BananaCupSessionRecord): number => {
    if (!session.jumps || session.jumps.length === 0) return 0;
    
    let totalRounds = 0;
    session.jumps.forEach((jump: BananaCupJumpRecord) => {
      // Count rounds that have distance data
      if (jump.firstRoundDistance) totalRounds++;
      if (jump.secondRoundDistance) totalRounds++;
      if (jump.thirdRoundDistance) totalRounds++;
      if (jump.fourthRoundDistance) totalRounds++;
      if (jump.fifthRoundDistance) totalRounds++;
    });
    
    return totalRounds;
  };

  const getSortedBananaCupSessions = () => {
    return [...bananaCupSessions].sort((a, b) => {
      let comparison = 0;
      switch (bananaCupSortField) {
        case 'date':
          comparison = new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime();
          break;
        case 'startTime':
          comparison = (a.startTime || '').localeCompare(b.startTime || '');
          break;
        case 'endTime':
          comparison = (a.endTime || '').localeCompare(b.endTime || '');
          break;
        case 'location':
          comparison = a.location.localeCompare(b.location);
          break;
        case 'hillSize':
          comparison = (a.hillSize || '').localeCompare(b.hillSize || '');
          break;
        case 'weather':
          comparison = a.weather.localeCompare(b.weather);
          break;
        case 'bananaCupName':
          comparison = a.bananaCupName.localeCompare(b.bananaCupName);
          break;
        case 'competitionNumber':
          comparison = a.competitionNumber - b.competitionNumber;
          break;
        case 'distance':
          const aTotalJumps = countBananaCupJumps(a);
          const bTotalJumps = countBananaCupJumps(b);
          comparison = aTotalJumps - bTotalJumps;
          break;
      }
      return bananaCupSortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Component to render a single session row
  const SessionRow = ({ session, columns }: { session: CompetitionSessionRecord; columns: { field: SortField; label: string; width?: string }[] }) => {
    const getCellValue = (field: SortField) => {
      switch (field) {
        case 'date':
          return session.date;
        case 'location':
          return session.location;
        case 'weather':
          if (!session.weather) return '-';
          const weatherValue = session.weather === 'indoor' ? 'INDOOR' : session.weather.replace(/[^a-zA-Z\s]/g, '').toUpperCase();
          return weatherValue;
        case 'category':
          if (session.isQualification) {
            return 'QUALIFICATION';
          }
          return session.category ? session.category.replace(/[^a-zA-Z\s]/g, '').toUpperCase() : '-';
        case 'distance':
          if (filterAthleteId) {
            // Count only competition jumps for the specific athlete (both rounds count as 2)
            const athleteJumps = session.competitionJumps?.filter(jump => {
              const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                ? (jump.athleteId as any)._id 
                : jump.athleteId as string;
              return jumpAthleteId === filterAthleteId;
            }) || [];
            return athleteJumps.length * 2;
          }
          return (session.competitionJumps?.length || 0) * 2;
        case 'hillSize':
          // Check if hillSize exists and is not empty
          if (session.hillSize && session.hillSize.trim() !== '') {
            // If it already starts with "HS", return as is, otherwise add "HS" prefix
            return session.hillSize.trim().toUpperCase().startsWith('HS') 
              ? session.hillSize.trim() 
              : `HS ${session.hillSize.trim()}`;
          }
          return '-';
        case 'trialRound':
          // Count how many jumps have trial round data
          const trialRoundCount = session.competitionJumps?.filter(jump => 
            jump.trialRoundStartingGate || jump.trialRoundDistance || jump.trialRoundWind || jump.trialRoundSpeed !== undefined
          ).length || 0;
          return trialRoundCount > 0 ? `${trialRoundCount}` : '-';
        case 'avgDistance': {
          if (session.competitionJumps && session.competitionJumps.length > 0) {
            const distances: number[] = [];
            session.competitionJumps.forEach(jump => {
              if (jump.firstRoundDistance && jump.firstRoundDistance > 0) {
                distances.push(jump.firstRoundDistance);
              }
              if (jump.secondRoundDistance && jump.secondRoundDistance > 0) {
                distances.push(jump.secondRoundDistance);
              }
            });
            if (distances.length === 0) return '-';
            const avg = distances.reduce((sum, d) => sum + d, 0) / distances.length;
            return `${avg.toFixed(1)}m`;
          }
          return '-';
        }
        default:
          return '-';
      }
    };

    return (
      <React.Fragment key={session._id}>
        <TableRow>
          <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem' }}>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                scrollPositionRef.current = window.scrollY;
                
                setExpandedSessions(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(session._id)) {
                    newSet.delete(session._id);
                  } else {
                    newSet.add(session._id);
                  }
                  return newSet;
                });
                
                setTimeout(() => {
                  window.scrollTo(0, scrollPositionRef.current);
                }, 0);
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                color: 'var(--text-gray)',
                display: 'inline-flex',
                alignItems: 'center',
                padding: 0
              }}
            >
              {expandedSessions.has(session._id) ? (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 4L6 8L10 4" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M4 2L8 6L4 10" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </TableCell>
          {columns.map(column => (
            <TableCell key={column.field} style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>
              {getCellValue(column.field)}
            </TableCell>
          ))}
          <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center', alignItems: 'center' }}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditSession(session);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  color: '#666',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Edit session"
              >
                <span style={{ fontSize: '0.75rem', color: '#333' }}>✎</span>
              </button>
              {(!currentSessionIdRef || currentSessionIdRef !== session._id) && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteCompetitionSession(session._id);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    color: '#dc3545',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Delete session"
                >
                  <span style={{ fontSize: '0.75rem' }}>🗑️</span>
                </button>
              )}
            </div>
          </TableCell>
        </TableRow>
        {expandedSessions.has(session._id) && (
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: '0', border: 'none' }}>
              <div style={{ 
                marginLeft: '30px', 
                background: 'rgba(0,0,0,0.02)',
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out'
              }}>
                {session.competitionJumps && session.competitionJumps.length > 0 && (
                  <table style={{ width: '100%', fontSize: '0.65rem' }}>
                    <thead>
                      <tr>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (competitionSortField === 'athlete') {
                              setCompetitionSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setCompetitionSortField('athlete');
                              setCompetitionSortDirection('asc');
                            }
                          }}
                        >
                          Athlete {competitionSortField === 'athlete' && (competitionSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th style={{ padding: '0.25rem', textAlign: 'left', fontWeight: 'bold' }}>Round</th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (competitionSortField === 'gate') {
                              setCompetitionSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setCompetitionSortField('gate');
                              setCompetitionSortDirection('asc');
                            }
                          }}
                        >
                          Gate {competitionSortField === 'gate' && (competitionSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (competitionSortField === 'distance') {
                              setCompetitionSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setCompetitionSortField('distance');
                              setCompetitionSortDirection('asc');
                            }
                          }}
                        >
                          Distance {competitionSortField === 'distance' && (competitionSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (competitionSortField === 'wind') {
                              setCompetitionSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setCompetitionSortField('wind');
                              setCompetitionSortDirection('asc');
                            }
                          }}
                        >
                          Wind {competitionSortField === 'wind' && (competitionSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (competitionSortField === 'speed') {
                              setCompetitionSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setCompetitionSortField('speed');
                              setCompetitionSortDirection('asc');
                            }
                          }}
                        >
                          Speed {competitionSortField === 'speed' && (competitionSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (competitionSortField === 'telemark') {
                              setCompetitionSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setCompetitionSortField('telemark');
                              setCompetitionSortDirection('asc');
                            }
                          }}
                        >
                          Telemark {competitionSortField === 'telemark' && (competitionSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (competitionSortField === 'suit') {
                              setCompetitionSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setCompetitionSortField('suit');
                              setCompetitionSortDirection('asc');
                            }
                          }}
                        >
                          Suit {competitionSortField === 'suit' && (competitionSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (competitionSortField === 'skis') {
                              setCompetitionSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setCompetitionSortField('skis');
                              setCompetitionSortDirection('asc');
                            }
                          }}
                        >
                          Skis {competitionSortField === 'skis' && (competitionSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (competitionSortField === 'shoes') {
                              setCompetitionSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setCompetitionSortField('shoes');
                              setCompetitionSortDirection('asc');
                            }
                          }}
                        >
                          Shoes {competitionSortField === 'shoes' && (competitionSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (competitionSortField === 'result') {
                              setCompetitionSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setCompetitionSortField('result');
                              setCompetitionSortDirection('asc');
                            }
                          }}
                        >
                          Result {competitionSortField === 'result' && (competitionSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        {session.isQualification ? (
                          <th style={{ padding: '0.25rem', textAlign: 'center', fontWeight: 'bold' }}>Rating</th>
                        ) : (
                          <>
                            <th style={{ padding: '0.25rem', textAlign: 'center', fontWeight: 'bold' }}>Trial R.</th>
                            <th style={{ padding: '0.25rem', textAlign: 'center', fontWeight: 'bold' }}>1st R.</th>
                            <th style={{ padding: '0.25rem', textAlign: 'center', fontWeight: 'bold' }}>2nd R.</th>
                          </>
                        )}
                        <th style={{ padding: '0.25rem', textAlign: 'center', fontWeight: 'bold' }}>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Filter competition jumps by athlete if filterAthleteId is provided
                        let filteredCompetitionJumps = session.competitionJumps;
                        if (filterAthleteId) {
                          filteredCompetitionJumps = session.competitionJumps.filter(jump => {
                            const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                              ? (jump.athleteId as any)._id 
                              : jump.athleteId as string;
                            return jumpAthleteId === filterAthleteId;
                          });
                        }

                        // Sort competition jumps
                        const sortedJumps = [...filteredCompetitionJumps].sort((a, b) => {
                          let comparison = 0;
                          switch (competitionSortField) {
                            case 'athlete':
                              comparison = getAthleteName(a.athleteId).localeCompare(getAthleteName(b.athleteId));
                              break;
                            case 'gate':
                              comparison = (a.firstRoundStartingGate || 0) - (b.firstRoundStartingGate || 0);
                              break;
                            case 'distance':
                              comparison = (a.firstRoundDistance || 0) - (b.firstRoundDistance || 0);
                              break;
                            case 'wind':
                              comparison = (a.firstRoundWind || '').localeCompare(b.firstRoundWind || '');
                              break;
                            case 'speed':
                              comparison = (a.firstRoundSpeed || 0) - (b.firstRoundSpeed || 0);
                              break;
                            case 'telemark':
                              comparison = (a.firstRoundTelemark === b.firstRoundTelemark) ? 0 : a.firstRoundTelemark ? 1 : -1;
                              break;
                            case 'result':
                              comparison = (a.result || 0) - (b.result || 0);
                              break;
                            case 'hillSize':
                              comparison = (a.hillSize || '').localeCompare(b.hillSize || '');
                              break;
                            case 'suit':
                              comparison = getEquipmentName(a.firstRoundSuitId || (a as any).suitId || '').localeCompare(getEquipmentName(b.firstRoundSuitId || (b as any).suitId || ''));
                              break;
                            case 'skis':
                              comparison = getEquipmentName(a.firstRoundSkisId || (a as any).skisId || '').localeCompare(getEquipmentName(b.firstRoundSkisId || (b as any).skisId || ''));
                              break;
                            case 'shoes':
                              comparison = getEquipmentName(a.firstRoundShoesId || (a as any).shoesId || '').localeCompare(getEquipmentName(b.firstRoundShoesId || (b as any).shoesId || ''));
                              break;
                            default:
                              comparison = 0;
                          }
                          return competitionSortDirection === 'asc' ? comparison : -comparison;
                        });

                        return sortedJumps.map(jump => {
                          // For qualification sessions, only show 1st round (the qualification jump)
                          const isQualification = session.isQualification === true;
                          
                          if (isQualification) {
                            // Qualification: only show 1st round
                            return (
                              <tr
                                key={jump._id}
                                style={isMobile ? { borderBottom: '2px solid #e0e6ed' } : undefined}
                              >
                                <td style={{ padding: '0.25rem' }}>{getAthleteName(jump.athleteId)}</td>
                                <td style={{ padding: '0.25rem' }}>Qualification</td>
                                <td style={{ padding: '0.25rem' }}>{jump.firstRoundStartingGate || '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.firstRoundDistance !== undefined && jump.firstRoundDistance !== null && !isNaN(jump.firstRoundDistance) ? `${jump.firstRoundDistance} m` : '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.firstRoundWind || '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.firstRoundSpeed !== undefined && jump.firstRoundSpeed !== null && !isNaN(jump.firstRoundSpeed) ? `${jump.firstRoundSpeed} km/h` : '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.firstRoundTelemark ? 'Yes' : 'No'}</td>
                                <td style={{ padding: '0.25rem' }}>{getEquipmentName(jump.firstRoundSuitId || (jump as any).suitId || '')}</td>
                                <td style={{ padding: '0.25rem' }}>{getEquipmentName(jump.firstRoundSkisId || (jump as any).skisId || '')}</td>
                                <td style={{ padding: '0.25rem' }}>{(jump.firstRoundShoesId || (jump as any).shoesId) ? getEquipmentName(jump.firstRoundShoesId || (jump as any).shoesId || '') : '-'}</td>
                                <td style={{ padding: '0.25rem', textAlign: 'center' }}>{jump.result || '-'}</td>
                                <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                                  <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    step="0.1"
                                    value={(jump as any).firstRoundRating ?? jump.rating ?? ''}
                                    onChange={(e) => {
                                      const value = parseFloat(e.target.value);
                                      if (!isNaN(value) && value >= 1 && value <= 10) {
                                        handleUpdateJumpRating(session._id, jump._id, 'first', value);
                                      } else if (e.target.value === '') {
                                        handleUpdateJumpRating(session._id, jump._id, 'first', 0);
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const r = (jump as any).firstRoundRating ?? jump.rating;
                                      if (e.target.value === '' || parseFloat(e.target.value) < 1 || parseFloat(e.target.value) > 10) {
                                        e.target.value = r ? String(r) : '';
                                      }
                                    }}
                                    style={{
                                      width: '60px',
                                      padding: '0.2rem 0.3rem',
                                      fontSize: '0.7rem',
                                      border: '1px solid #e0e6ed',
                                      borderRadius: '0.25rem',
                                      textAlign: 'center'
                                    }}
                                    placeholder="1-10"
                                  />
                                </td>
                                <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleEditJump(session._id, jump);
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: '1rem',
                                      color: '#666',
                                      padding: '0.25rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      margin: '0 auto'
                                    }}
                                    title="Edit jump"
                                  >
                                    <span style={{ fontSize: '0.75rem', color: '#333' }}>✎</span>
                                  </button>
                                </td>
                              </tr>
                            );
                          }
                          
                          // Regular competition: show trial, 1st, and 2nd rounds
                          // Check if trial round has any data (more comprehensive check)
                          const hasTrialRound = !!(jump.trialRoundStartingGate || 
                            jump.trialRoundDistance || 
                            jump.trialRoundWind || 
                            (jump.trialRoundSpeed !== undefined && jump.trialRoundSpeed !== null) ||
                            jump.trialRoundTelemark !== undefined);
                          const rowSpan = hasTrialRound ? 3 : 2;
                          
                          return (
                            <React.Fragment key={jump._id}>
                              {hasTrialRound && (
                                <tr style={{ backgroundColor: '#f5f5f5', color: '#888' }}>
                                  <td style={{ padding: '0.25rem' }} rowSpan={rowSpan}>{getAthleteName(jump.athleteId)}</td>
                                  <td style={{ padding: '0.25rem', fontStyle: 'italic' }}>Trial</td>
                                  <td style={{ padding: '0.25rem' }}>{jump.trialRoundStartingGate || '-'}</td>
                                  <td style={{ padding: '0.25rem' }}>{jump.trialRoundDistance !== undefined && jump.trialRoundDistance !== null && !isNaN(jump.trialRoundDistance) ? `${jump.trialRoundDistance} m` : '-'}</td>
                                  <td style={{ padding: '0.25rem' }}>{jump.trialRoundWind || '-'}</td>
                                  <td style={{ padding: '0.25rem' }}>{jump.trialRoundSpeed !== undefined && jump.trialRoundSpeed !== null && !isNaN(jump.trialRoundSpeed) ? `${jump.trialRoundSpeed} km/h` : '-'}</td>
                                  <td style={{ padding: '0.25rem' }}>{jump.trialRoundTelemark ? 'Yes' : 'No'}</td>
                                  <td style={{ padding: '0.25rem' }}>{getEquipmentName(jump.trialRoundSuitId || (jump as any).suitId || '')}</td>
                                  <td style={{ padding: '0.25rem' }}>{getEquipmentName(jump.trialRoundSkisId || (jump as any).skisId || '')}</td>
                                  <td style={{ padding: '0.25rem' }}>{(jump.trialRoundShoesId || (jump as any).shoesId) ? getEquipmentName(jump.trialRoundShoesId || (jump as any).shoesId || '') : '-'}</td>
                                  <td style={{ padding: '0.25rem', textAlign: 'center' }} rowSpan={rowSpan}>{jump.result || '-'}</td>
                                  <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                                    <input
                                      type="number"
                                      min="1"
                                      max="10"
                                      step="0.1"
                                      value={(jump as any).trialRoundRating ?? ''}
                                      onChange={(e) => {
                                        const value = parseFloat(e.target.value);
                                        if (!isNaN(value) && value >= 1 && value <= 10) {
                                          handleUpdateJumpRating(session._id, jump._id, 'trial', value);
                                        } else if (e.target.value === '') {
                                          handleUpdateJumpRating(session._id, jump._id, 'trial', 0);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        const r = (jump as any).trialRoundRating;
                                        if (e.target.value === '' || parseFloat(e.target.value) < 1 || parseFloat(e.target.value) > 10) {
                                          e.target.value = r != null ? String(r) : '';
                                        }
                                      }}
                                      style={{ width: '60px', padding: '0.2rem 0.3rem', fontSize: '0.7rem', border: '1px solid #e0e6ed', borderRadius: '0.25rem', textAlign: 'center' }}
                                      placeholder="1-10"
                                    />
                                  </td>
                                  <td style={{ padding: '0.25rem', textAlign: 'center' }}>{(jump as any).firstRoundRating ?? jump.rating ?? '-'}</td>
                                  <td style={{ padding: '0.25rem', textAlign: 'center' }}>{(jump as any).secondRoundRating ?? '-'}</td>
                                  <td style={{ padding: '0.25rem', textAlign: 'center' }} rowSpan={rowSpan}>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleEditJump(session._id, jump);
                                      }}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        color: '#666',
                                        padding: '0.25rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto'
                                      }}
                                      title="Edit jump"
                                    >
                                      <span style={{ fontSize: '0.75rem', color: '#333' }}>✎</span>
                                    </button>
                                  </td>
                                </tr>
                              )}
                              <tr>
                                {!hasTrialRound && (
                                  <td style={{ padding: '0.25rem' }} rowSpan={rowSpan}>{getAthleteName(jump.athleteId)}</td>
                                )}
                                <td style={{ padding: '0.25rem' }}>1st</td>
                                <td style={{ padding: '0.25rem' }}>{jump.firstRoundStartingGate || '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.firstRoundDistance !== undefined && jump.firstRoundDistance !== null && !isNaN(jump.firstRoundDistance) ? `${jump.firstRoundDistance} m` : '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.firstRoundWind || '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.firstRoundSpeed !== undefined && jump.firstRoundSpeed !== null && !isNaN(jump.firstRoundSpeed) ? `${jump.firstRoundSpeed} km/h` : '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.firstRoundTelemark ? 'Yes' : 'No'}</td>
                                <td style={{ padding: '0.25rem' }}>{getEquipmentName(jump.firstRoundSuitId || (jump as any).suitId || '')}</td>
                                <td style={{ padding: '0.25rem' }}>{getEquipmentName(jump.firstRoundSkisId || (jump as any).skisId || '')}</td>
                                <td style={{ padding: '0.25rem' }}>{(jump.firstRoundShoesId || (jump as any).shoesId) ? getEquipmentName(jump.firstRoundShoesId || (jump as any).shoesId || '') : '-'}</td>
                                {!hasTrialRound && (
                                  <td style={{ padding: '0.25rem', textAlign: 'center' }} rowSpan={rowSpan}>{jump.result || '-'}</td>
                                )}
                                {hasTrialRound && (
                                  <>
                                    <td style={{ padding: '0.25rem', textAlign: 'center' }}>{(jump as any).trialRoundRating ?? '-'}</td>
                                    <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                                      <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        step="0.1"
                                        value={(jump as any).firstRoundRating ?? jump.rating ?? ''}
                                        onChange={(e) => {
                                          const value = parseFloat(e.target.value);
                                          if (!isNaN(value) && value >= 1 && value <= 10) {
                                            handleUpdateJumpRating(session._id, jump._id, 'first', value);
                                          } else if (e.target.value === '') {
                                            handleUpdateJumpRating(session._id, jump._id, 'first', 0);
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const r = (jump as any).firstRoundRating ?? jump.rating;
                                          if (e.target.value === '' || parseFloat(e.target.value) < 1 || parseFloat(e.target.value) > 10) {
                                            e.target.value = r != null ? String(r) : '';
                                          }
                                        }}
                                        style={{ width: '60px', padding: '0.2rem 0.3rem', fontSize: '0.7rem', border: '1px solid #e0e6ed', borderRadius: '0.25rem', textAlign: 'center' }}
                                        placeholder="1-10"
                                      />
                                    </td>
                                    <td style={{ padding: '0.25rem', textAlign: 'center' }}>{(jump as any).secondRoundRating ?? '-'}</td>
                                  </>
                                )}
                                {!hasTrialRound && (
                                  <td style={{ padding: '0.25rem', textAlign: 'center' }}>{(jump as any).trialRoundRating ?? '-'}</td>
                                )}
                                {!hasTrialRound && (
                                  <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                                    <input
                                      type="number"
                                      min="1"
                                      max="10"
                                      step="0.1"
                                      value={(jump as any).firstRoundRating ?? jump.rating ?? ''}
                                      onChange={(e) => {
                                        const value = parseFloat(e.target.value);
                                        if (!isNaN(value) && value >= 1 && value <= 10) {
                                          handleUpdateJumpRating(session._id, jump._id, 'first', value);
                                        } else if (e.target.value === '') {
                                          handleUpdateJumpRating(session._id, jump._id, 'first', 0);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        const r = (jump as any).firstRoundRating ?? jump.rating;
                                        if (e.target.value === '' || parseFloat(e.target.value) < 1 || parseFloat(e.target.value) > 10) {
                                          e.target.value = r != null ? String(r) : '';
                                        }
                                      }}
                                      style={{ width: '60px', padding: '0.2rem 0.3rem', fontSize: '0.7rem', border: '1px solid #e0e6ed', borderRadius: '0.25rem', textAlign: 'center' }}
                                      placeholder="1-10"
                                    />
                                  </td>
                                )}
                                {!hasTrialRound && (
                                  <td style={{ padding: '0.25rem', textAlign: 'center' }}>{(jump as any).secondRoundRating ?? '-'}</td>
                                )}
                                {!hasTrialRound && (
                                  <td style={{ padding: '0.25rem', textAlign: 'center' }} rowSpan={rowSpan}>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleEditJump(session._id, jump);
                                      }}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        color: '#666',
                                        padding: '0.25rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto'
                                      }}
                                      title="Edit jump"
                                    >
                                      <span style={{ fontSize: '0.75rem', color: '#333' }}>✎</span>
                                    </button>
                                  </td>
                                )}
                              </tr>
                              <tr style={isMobile ? { borderBottom: '2px solid #e0e6ed' } : undefined}>
                                <td style={{ padding: '0.25rem' }}>2nd</td>
                                <td style={{ padding: '0.25rem' }}>{jump.secondRoundStartingGate || '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.secondRoundDistance !== undefined && jump.secondRoundDistance !== null && !isNaN(jump.secondRoundDistance) ? `${jump.secondRoundDistance} m` : '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.secondRoundWind || '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.secondRoundSpeed !== undefined && jump.secondRoundSpeed !== null && !isNaN(jump.secondRoundSpeed) ? `${jump.secondRoundSpeed} km/h` : '-'}</td>
                                <td style={{ padding: '0.25rem' }}>{jump.secondRoundTelemark ? 'Yes' : 'No'}</td>
                                <td style={{ padding: '0.25rem' }}>{getEquipmentName(jump.secondRoundSuitId || (jump as any).suitId || '')}</td>
                                <td style={{ padding: '0.25rem' }}>{getEquipmentName(jump.secondRoundSkisId || (jump as any).skisId || '')}</td>
                                <td style={{ padding: '0.25rem' }}>{(jump.secondRoundShoesId || (jump as any).shoesId) ? getEquipmentName(jump.secondRoundShoesId || (jump as any).shoesId || '') : '-'}</td>
                                {hasTrialRound && (
                                  <>
                                    <td style={{ padding: '0.25rem', textAlign: 'center' }}>{(jump as any).trialRoundRating ?? '-'}</td>
                                    <td style={{ padding: '0.25rem', textAlign: 'center' }}>{(jump as any).firstRoundRating ?? jump.rating ?? '-'}</td>
                                    <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                                      <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        step="0.1"
                                        value={(jump as any).secondRoundRating ?? ''}
                                        onChange={(e) => {
                                          const value = parseFloat(e.target.value);
                                          if (!isNaN(value) && value >= 1 && value <= 10) {
                                            handleUpdateJumpRating(session._id, jump._id, 'second', value);
                                          } else if (e.target.value === '') {
                                            handleUpdateJumpRating(session._id, jump._id, 'second', 0);
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const r = (jump as any).secondRoundRating;
                                          if (e.target.value === '' || parseFloat(e.target.value) < 1 || parseFloat(e.target.value) > 10) {
                                            e.target.value = r != null ? String(r) : '';
                                          }
                                        }}
                                        style={{ width: '60px', padding: '0.2rem 0.3rem', fontSize: '0.7rem', border: '1px solid #e0e6ed', borderRadius: '0.25rem', textAlign: 'center' }}
                                        placeholder="1-10"
                                      />
                                    </td>
                                  </>
                                )}
                              </tr>
                            </React.Fragment>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                )}
                {session.isCompleted && (
                  <div style={{ 
                    padding: '1rem', 
                    marginTop: '1rem',
                    background: 'rgba(255, 150, 0, 0.05)',
                    borderRadius: '0.5rem',
                    border: '1px solid rgba(255, 150, 0, 0.2)'
                  }}>
                    <h4 style={{ 
                      fontSize: '0.85rem', 
                      fontWeight: 700, 
                      marginBottom: '0.75rem',
                      color: 'var(--accent-orange)',
                      fontFamily: "'Poppins', sans-serif"
                    }}>
                      Session Summary
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                      {session.coachDescription && (
                        <div>
                          <strong style={{ fontSize: '0.75rem', color: '#666' }}>Coach's Notes:</strong>
                          <p style={{ fontSize: '0.7rem', color: '#333', marginTop: '0.25rem', lineHeight: '1.4' }}>
                            {session.coachDescription}
                          </p>
                        </div>
                      )}
                      {(() => {
                        // Calculate stats per athlete
                        const athleteStats = new Map<string, {
                          ratings: number[];
                          distances: number[];
                          speeds: number[];
                        }>();
                        
                        session.competitionJumps.forEach(jump => {
                          const athleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                            ? (jump.athleteId as any)._id 
                            : jump.athleteId as string;
                          if (!athleteStats.has(athleteId)) {
                            athleteStats.set(athleteId, { ratings: [], distances: [], speeds: [] });
                          }
                          const stats = athleteStats.get(athleteId)!;
                          if (jump.rating) stats.ratings.push(jump.rating);
                          // For qualification sessions, only use 1st round data
                          if (session.isQualification === true) {
                            if (jump.firstRoundDistance) stats.distances.push(jump.firstRoundDistance);
                            if (jump.firstRoundSpeed) stats.speeds.push(jump.firstRoundSpeed);
                          } else {
                            // For regular competitions, collect distances from all rounds
                            if (jump.firstRoundDistance) stats.distances.push(jump.firstRoundDistance);
                            if (jump.secondRoundDistance) stats.distances.push(jump.secondRoundDistance);
                            if (jump.trialRoundDistance) stats.distances.push(jump.trialRoundDistance);
                            // Collect speeds from all rounds
                            if (jump.firstRoundSpeed) stats.speeds.push(jump.firstRoundSpeed);
                            if (jump.secondRoundSpeed) stats.speeds.push(jump.secondRoundSpeed);
                            if (jump.trialRoundSpeed) stats.speeds.push(jump.trialRoundSpeed);
                          }
                        });
                        
                        const athleteStatsArray = Array.from(athleteStats.entries()).map(([athleteId, stats]) => ({
                          athleteId,
                          athleteName: getAthleteName(athleteId),
                          avgRating: stats.ratings.length > 0 
                            ? (stats.ratings.reduce((a, b) => a + b, 0) / stats.ratings.length).toFixed(1)
                            : null,
                          longestDistance: stats.distances.length > 0 
                            ? Math.max(...stats.distances).toFixed(1)
                            : null,
                          avgSpeed: stats.speeds.length > 0
                            ? (stats.speeds.reduce((a, b) => a + b, 0) / stats.speeds.length).toFixed(1)
                            : null
                        }));
                        
                        return athleteStatsArray.map(({ athleteId, athleteName, avgRating, longestDistance, avgSpeed }) => (
                          <div key={athleteId} style={{ 
                            padding: '0.5rem',
                            background: 'rgba(255, 255, 255, 0.5)',
                            borderRadius: '0.25rem'
                          }}>
                            <strong style={{ fontSize: '0.75rem', color: 'var(--accent-orange)' }}>{athleteName}:</strong>
                            <div style={{ fontSize: '0.65rem', color: '#666', marginTop: '0.25rem', lineHeight: '1.5' }}>
                              {avgRating && <div>Avg Rating: {avgRating}/10</div>}
                              {longestDistance && <div>Longest: {longestDistance}m</div>}
                              {avgSpeed && <div>Avg Speed: {avgSpeed} km/h</div>}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  // Component to render Banana Cup Sessions table with scroll indicator
  const BananaCupSessionsTable = () => {
    // State for scroll indicator
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    
    // Check if table can scroll horizontally
    useEffect(() => {
      if (!isMobile || !scrollContainerRef.current) return;
      
      const checkScroll = () => {
        const container = scrollContainerRef.current;
        if (container) {
          const canScroll = container.scrollWidth > container.clientWidth;
          const isScrolledToEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
          setShowScrollIndicator(canScroll && !isScrolledToEnd);
        }
      };
      
      checkScroll();
      const container = scrollContainerRef.current;
      if (container) {
        container.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);
        return () => {
          container.removeEventListener('scroll', checkScroll);
          window.removeEventListener('resize', checkScroll);
        };
      }
    }, [isMobile, getSortedBananaCupSessions()]);
    
    return (
      <RecordsSection>
        <div style={{ 
          fontWeight: 900, 
          fontSize: '1.1rem', 
          marginBottom: '1rem',
          color: 'var(--accent-orange)',
          fontFamily: "'Poppins', sans-serif",
          position: 'relative',
          paddingLeft: '1rem',
          letterSpacing: '-0.02em'
        }}>
          <span style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '6px',
            height: '100%',
            background: 'var(--accent-orange)'
          }}></span>
          Banana Cup Sessions:
        </div>
        <div 
          ref={scrollContainerRef}
          style={{ 
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            width: '100%',
            position: 'relative',
            ...(isMobile ? { marginLeft: '-1.5rem', marginRight: '-1.5rem', paddingLeft: '1.5rem', paddingRight: '1.5rem' } : {})
          }}
        >
          <RecordsTable style={{ fontSize: '0.75rem', width: '100%', tableLayout: isMobile ? 'auto' : 'fixed', minWidth: isMobile ? '600px' : 'auto' }}>
            <thead>
              <tr>
                <th style={{ width: '30px', fontSize: '0.65rem', fontWeight: 'normal', padding: '0.25rem' }}></th>
                <TableHeader 
                  onClick={() => handleBananaCupSort('date')}
                  style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    padding: '0.25rem',
                    textAlign: 'center'
                  }}
                >
                  Date {bananaCupSortField === 'date' && (bananaCupSortDirection === 'asc' ? '↑' : '↓')}
                </TableHeader>
                <TableHeader 
                  onClick={() => handleBananaCupSort('bananaCupName')}
                  style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    padding: '0.25rem',
                    textAlign: 'center'
                  }}
                >
                  Banana Cup {bananaCupSortField === 'bananaCupName' && (bananaCupSortDirection === 'asc' ? '↑' : '↓')}
                </TableHeader>
                <TableHeader 
                  onClick={() => handleBananaCupSort('competitionNumber')}
                  style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    padding: '0.25rem',
                    textAlign: 'center'
                  }}
                >
                  Competition {bananaCupSortField === 'competitionNumber' && (bananaCupSortDirection === 'asc' ? '↑' : '↓')}
                </TableHeader>
                <TableHeader 
                  onClick={() => handleBananaCupSort('location')}
                  style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    padding: '0.25rem',
                    textAlign: 'center'
                  }}
                >
                  Location {bananaCupSortField === 'location' && (bananaCupSortDirection === 'asc' ? '↑' : '↓')}
                </TableHeader>
                <TableHeader 
                  onClick={() => handleBananaCupSort('hillSize')}
                  style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    padding: '0.25rem',
                    textAlign: 'center'
                  }}
                >
                  Hill Size {bananaCupSortField === 'hillSize' && (bananaCupSortDirection === 'asc' ? '↑' : '↓')}
                </TableHeader>
                <TableHeader 
                  onClick={() => handleBananaCupSort('weather')}
                  style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    padding: '0.25rem',
                    textAlign: 'center'
                  }}
                >
                  Weather {bananaCupSortField === 'weather' && (bananaCupSortDirection === 'asc' ? '↑' : '↓')}
                </TableHeader>
                <TableHeader 
                  onClick={() => handleBananaCupSort('distance')}
                  style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    padding: '0.25rem',
                    textAlign: 'center'
                  }}
                >
                  Jumps {bananaCupSortField === 'distance' && (bananaCupSortDirection === 'asc' ? '↑' : '↓')}
                </TableHeader>
                <th style={{ fontSize: '0.65rem', fontWeight: 'bold', padding: '0.25rem', textAlign: 'center' }}>Position</th>
                <th style={{ fontSize: '0.65rem', fontWeight: 'bold', padding: '0.25rem', textAlign: 'center' }}>Points</th>
                <th style={{ fontSize: '0.65rem', fontWeight: 'bold', padding: '0.25rem', textAlign: 'center' }}>Total Distance</th>
                <th style={{ width: '80px', fontSize: '0.65rem', fontWeight: 'bold', padding: '0.25rem', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getSortedBananaCupSessions().map(session => {
                const getCellValue = (field: BananaCupSortField) => {
                  switch (field) {
                    case 'date':
                      return session.date ? new Date(session.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';
                    case 'bananaCupName':
                      return session.bananaCupName || '-';
                    case 'competitionNumber':
                      return session.competitionNumber ? `#${session.competitionNumber}` : '-';
                    case 'location':
                      return session.location || '-';
                    case 'hillSize':
                      return session.hillSize || '-';
                    case 'weather':
                      return session.weather || '-';
                    case 'distance':
                      return countBananaCupJumps(session);
                    default:
                      return '-';
                  }
                };
                
                const isExpanded = expandedBananaCupSessions.has(session._id);
                const toggleExpansion = () => {
                  setExpandedBananaCupSessions(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(session._id)) {
                      newSet.delete(session._id);
                    } else {
                      newSet.add(session._id);
                    }
                    return newSet;
                  });
                };
                
                return (
                  <React.Fragment key={session._id}>
                    <TableRow 
                      style={{ cursor: 'pointer' }}
                      onClick={toggleExpansion}
                    >
                      <TableCell style={{ width: '30px', fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          {isExpanded ? (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M2 4L6 8L10 4" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M4 2L8 6L4 10" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </span>
                      </TableCell>
                      <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>{getCellValue('date')}</TableCell>
                      <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>{getCellValue('bananaCupName')}</TableCell>
                      <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>{getCellValue('competitionNumber')}</TableCell>
                      <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>{getCellValue('location')}</TableCell>
                      <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>{getCellValue('hillSize')}</TableCell>
                      <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>{getCellValue('weather')}</TableCell>
                      <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>{getCellValue('distance')}</TableCell>
                      <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>{session.jumps && session.jumps.length > 0 ? session.jumps[0].position || '-' : '-'}</TableCell>
                      <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>{session.jumps && session.jumps.length > 0 ? session.jumps[0].points || '-' : '-'}</TableCell>
                      <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>{session.jumps && session.jumps.length > 0 ? session.jumps[0].totalDistance || '-' : '-'}</TableCell>
                      <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center', alignItems: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Note: Banana Cup sessions are read-only as they come from finalized competitions
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'not-allowed',
                              fontSize: '1rem',
                              color: '#ccc',
                              padding: '0.25rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Banana Cup sessions are managed through the Banana Cup page"
                            disabled
                          >
                            <span style={{ fontSize: '0.75rem', color: '#ccc' }}>✎</span>
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={11} style={{ padding: '1rem', background: 'rgba(0, 0, 0, 0.02)' }}>
                          <div>
                            <div style={{ marginBottom: '0.5rem', fontWeight: 700, fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                              Session Details: {session.startTime || '-'} - {session.endTime || '-'} • Weather: {session.weather || '-'}
                            </div>
                            {session.jumps && session.jumps.length > 0 ? (
                              <table style={{ width: '100%', fontSize: '0.65rem', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Round</th>
                                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Gate</th>
                                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Distance</th>
                                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Wind</th>
                                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Speed</th>
                                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Telemark</th>
                                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Position</th>
                                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Points</th>
                                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Suit</th>
                                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Skis</th>
                                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Shoes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    // Sort Banana Cup jumps - default to position (winner first)
                                    const sortedJumps = [...session.jumps].sort((a, b) => {
                                      let comparison = 0;
                                      switch (bananaCupJumpSortField) {
                                        case 'position':
                                          comparison = (a.position || 999) - (b.position || 999);
                                          break;
                                        case 'points':
                                          comparison = (b.points || 0) - (a.points || 0);
                                          break;
                                        case 'totalDistance':
                                          comparison = (b.totalDistance || 0) - (a.totalDistance || 0);
                                          break;
                                        case 'gate':
                                          comparison = (a.firstRoundStartingGate || 0) - (b.firstRoundStartingGate || 0);
                                          break;
                                        case 'distance':
                                          comparison = (b.firstRoundDistance || 0) - (a.firstRoundDistance || 0);
                                          break;
                                        case 'wind':
                                          comparison = (a.firstRoundWind || '').localeCompare(b.firstRoundWind || '');
                                          break;
                                        case 'speed':
                                          comparison = (a.firstRoundSpeed || 0) - (b.firstRoundSpeed || 0);
                                          break;
                                        case 'telemark':
                                          comparison = (a.firstRoundTelemark ? 1 : 0) - (b.firstRoundTelemark ? 1 : 0);
                                          break;
                                        default:
                                          comparison = (a.position || 999) - (b.position || 999);
                                      }
                                      return bananaCupJumpSortDirection === 'asc' ? comparison : -comparison;
                                    });

                                    return sortedJumps.map(jump => (
                                      <React.Fragment key={jump._id}>
                                        <tr>
                                          <td style={{ padding: '0.25rem' }} rowSpan={2}>1st</td>
                                          <td style={{ padding: '0.25rem' }}>{jump.firstRoundStartingGate || '-'}</td>
                                          <td style={{ padding: '0.25rem' }}>{jump.firstRoundDistance !== undefined && jump.firstRoundDistance !== null && !isNaN(jump.firstRoundDistance) ? `${jump.firstRoundDistance} m` : '-'}</td>
                                          <td style={{ padding: '0.25rem' }}>{jump.firstRoundWind || '-'}</td>
                                          <td style={{ padding: '0.25rem' }}>{jump.firstRoundSpeed !== undefined && jump.firstRoundSpeed !== null && !isNaN(jump.firstRoundSpeed) ? `${jump.firstRoundSpeed} km/h` : '-'}</td>
                                          <td style={{ padding: '0.25rem' }}>{jump.firstRoundTelemark ? 'Yes' : 'No'}</td>
                                          <td style={{ padding: '0.25rem' }} rowSpan={2}>{jump.position || '-'}</td>
                                          <td style={{ padding: '0.25rem' }} rowSpan={2}>{jump.points || '-'}</td>
                                          <td style={{ padding: '0.25rem' }} rowSpan={2}>{getEquipmentName(jump.suitId)}</td>
                                          <td style={{ padding: '0.25rem' }} rowSpan={2}>{getEquipmentName(jump.skisId)}</td>
                                          <td style={{ padding: '0.25rem' }} rowSpan={2}>{jump.shoesId ? getEquipmentName(jump.shoesId) : '-'}</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
                                          <td style={{ padding: '0.25rem' }}>{jump.secondRoundStartingGate || '-'}</td>
                                          <td style={{ padding: '0.25rem' }}>{jump.secondRoundDistance !== undefined && jump.secondRoundDistance !== null && !isNaN(jump.secondRoundDistance) ? `${jump.secondRoundDistance} m` : '-'}</td>
                                          <td style={{ padding: '0.25rem' }}>{jump.secondRoundWind || '-'}</td>
                                          <td style={{ padding: '0.25rem' }}>{jump.secondRoundSpeed !== undefined && jump.secondRoundSpeed !== null && !isNaN(jump.secondRoundSpeed) ? `${jump.secondRoundSpeed} km/h` : '-'}</td>
                                          <td style={{ padding: '0.25rem' }}>{jump.secondRoundTelemark ? 'Yes' : 'No'}</td>
                                        </tr>
                                      </React.Fragment>
                                    ));
                                  })()}
                                </tbody>
                              </table>
                            ) : (
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-gray)', fontStyle: 'italic' }}>
                                No jumps recorded for this session.
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </RecordsTable>
        </div>
        {/* Scroll indicator text - below orange line */}
        {isMobile && showScrollIndicator && (
          <div style={{
            fontSize: '0.65rem',
            color: '#000',
            fontFamily: "'Poppins', sans-serif",
            marginTop: '1rem',
            paddingLeft: '1rem',
            fontWeight: 400
          }}>
            Scroll sideways to see all data
          </div>
        )}
      </RecordsSection>
    );
  };

  // Component to render competition sessions table
  const CompetitionSessionsTable = ({ 
    title, 
    sessions, 
    columns 
  }: { 
    title: string; 
    sessions: CompetitionSessionRecord[]; 
    columns: { field: SortField; label: string; width?: string }[] 
  }) => {
    // State for scroll indicator
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    
    // Check if table can scroll horizontally
    useEffect(() => {
      if (!isMobile || !scrollContainerRef.current) return;
      
      const checkScroll = () => {
        const container = scrollContainerRef.current;
        if (container) {
          const canScroll = container.scrollWidth > container.clientWidth;
          const isScrolledToEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
          setShowScrollIndicator(canScroll && !isScrolledToEnd);
        }
      };
      
      checkScroll();
      const container = scrollContainerRef.current;
      if (container) {
        container.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);
        return () => {
          container.removeEventListener('scroll', checkScroll);
          window.removeEventListener('resize', checkScroll);
        };
      }
    }, [isMobile, sessions]);
    
    return (
      <RecordsSection>
        <div style={{ 
          fontWeight: 900, 
          fontSize: '1.1rem', 
          marginBottom: '1rem',
          color: 'var(--accent-orange)',
          fontFamily: "'Poppins', sans-serif",
          position: 'relative',
          paddingLeft: '1rem',
          letterSpacing: '-0.02em'
        }}>
          <span style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '6px',
            height: '100%',
            background: 'var(--accent-orange)'
          }}></span>
          {title}:
        </div>
        <div 
          ref={scrollContainerRef}
          style={{ 
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            width: '100%',
            position: 'relative',
            ...(isMobile ? { marginLeft: '-1.5rem', marginRight: '-1.5rem', paddingLeft: '1.5rem', paddingRight: '1.5rem' } : {})
          }}
        >
          <RecordsTable style={{ fontSize: '0.75rem', width: '100%', tableLayout: isMobile ? 'auto' : 'fixed', minWidth: isMobile ? '600px' : 'auto' }}>
            <thead>
              <tr>
                <th style={{ width: isMobile ? '20px' : '30px', fontSize: '0.65rem', fontWeight: 'normal', padding: isMobile ? '0.25rem 0.1rem' : '0.25rem' }}></th>
                {columns.map(column => (
                  <SortableHeader key={column.field} field={column.field} style={{ width: column.width }}>
                    {column.label}
                  </SortableHeader>
                ))}
                <th style={{ width: '80px', fontSize: '0.65rem', fontWeight: 'bold', padding: '0.25rem', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(session => (
                <SessionRow key={session._id} session={session} columns={columns} />
              ))}
            </tbody>
          </RecordsTable>
        </div>
        {/* Scroll indicator text - below orange line */}
        {isMobile && showScrollIndicator && (
          <div style={{
            fontSize: '0.65rem',
            color: '#000',
            fontFamily: "'Poppins', sans-serif",
            marginTop: '1rem',
            paddingLeft: '1rem',
            fontWeight: 400
          }}>
            Scroll sideways to see all data
          </div>
        )}
      </RecordsSection>
    );
  };

  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode; style?: React.CSSProperties }> = ({ field, children, style }) => (
    <TableHeader 
      onClick={() => handleSort(field)}
      style={{ 
        cursor: 'pointer', 
        userSelect: 'none',
        fontSize: '0.65rem',
        fontWeight: 'bold',
        padding: '0.25rem',
        textAlign: 'center',
        ...style
      }}
    >
      {children}
      {sortField === field && (
        <span style={{ marginLeft: '0.25rem' }}>
          {sortDirection === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </TableHeader>
  );

  const currentSessionIdRef = typeof window !== 'undefined' ? (window as any).currentSessionId : null;
  const currentSessionRecord = currentSessionIdRef
    ? competitionSessions.find(session => session._id === currentSessionIdRef) || null
    : null;
  const currentSessionIsQualification = activeSessionIsQualification || !!currentSessionRecord?.isQualification;
  const currentSessionIsPaused = !!currentSessionRecord?.isPaused;

  if (isLoading) {
    return (
      <TrainingSessionContainer>
        <PageHeader>
          <PageTitle>Competition Sessions</PageTitle>
        </PageHeader>
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-gray)' }}>
          Loading competition sessions...
        </div>
      </TrainingSessionContainer>
    );
  }

  const notificationOverlay = notification ? (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
        background: notification.type === 'success' ? '#1f8b4c' : '#a30000',
        color: '#fff',
        padding: '0.85rem 1.5rem',
        borderRadius: '999px',
        fontSize: '1rem',
        fontWeight: 600,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        pointerEvents: 'none',
        fontFamily: "'Poppins', sans-serif"
      }}
    >
      {notification.message}
    </div>
  ) : null;

  const renderEquipmentMessage = (field: 'suit' | 'skis' | 'shoes' | 'bindings') => {
    if (!formData.athleteId) return null;
    const selectedAthlete = athletes.find(a => a._id === formData.athleteId);
    const athletePlan = (selectedAthlete as any)?.subscription?.plan || 'free';
    const hasEquipmentPlan = hasPlanOrHigher(athletePlan, 'hs60');
    const countByType = {
      suit: getEquipmentByType('suit', formData.athleteId).length,
      skis: getEquipmentByType('skis', formData.athleteId).length,
      shoes: getEquipmentByType('shoes', formData.athleteId).length,
      bindings: getEquipmentByType('bindings', formData.athleteId).length,
    };
    const typeLabel = { suit: 'suits', skis: 'skis', shoes: 'shoes', bindings: 'bindings' };
    const hasNoneOfType = countByType[field] === 0;
    if (!hasEquipmentPlan) {
      return (
        <div style={{ marginBottom: '1rem', marginTop: '0.5rem', padding: '1rem', background: '#fff8f0', border: '1px solid var(--accent-orange)', borderRadius: '8px' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-gray)' }}>
            Adding equipment is available on HS60 and higher plans.
          </p>
          <SubmitButton type="button" onClick={() => navigate('/pricing')} style={{ marginTop: '0.75rem' }}>
            View plans
          </SubmitButton>
        </div>
      );
    }
    if (hasNoneOfType) {
      const equipmentPath = user?.role === 'coach' ? '/coach/equipment' : '/jumper/equipment';
      return (
        <div style={{ marginBottom: '1rem', marginTop: '0.5rem', padding: '1rem', background: 'var(--bg-sidebar)', border: '1px solid var(--card-border)', borderRadius: '8px' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-gray)' }}>
            You haven&apos;t added any {typeLabel[field]} yet.
          </p>
          <SubmitButton type="button" onClick={() => navigate(equipmentPath)} style={{ marginTop: '0.75rem' }}>
            Add equipment
          </SubmitButton>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {notificationOverlay}
      <TrainingSessionContainer>
        <PageHeader style={{ marginTop: '2.5rem' }}>
          <PageTitle>Competition Sessions</PageTitle>
          <PageSubtitle>
            Track all competition data and results for your athletes. Add data after competition is completed.
          </PageSubtitle>
        </PageHeader>

      {/* Edit mode notification */}
      {(editingSessionId || editingJumpId) && (
        <div style={{ 
          background: 'rgba(255, 193, 7, 0.2)', 
          border: '2px solid #ffc107', 
          borderRadius: '8px', 
          padding: '1rem', 
          marginBottom: '1rem',
          color: '#856404',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span>⚠️</span>
          <span>You are currently editing data. Changes will be saved when you submit the form.</span>
        </div>
      )}

      {error && error !== 'invalid_end_time' && error !== 'no_athletes_selected' && (
        <div style={{ 
          background: 'rgba(255, 0, 0, 0.1)', 
          border: '1px solid #ff0000', 
          borderRadius: '8px', 
          padding: '1rem', 
          marginBottom: '1rem',
          color: '#ff0000'
        }}>
          {error}
        </div>
      )}
      
      {!showNewCompetitionSessionForm ? (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem',
          marginBottom: '0.5rem',
          ...(isMobile ? { flexDirection: 'column', width: '100%' } : {})
        }}>
          <SubmitButton
            onClick={() => {
              setIsQualificationMode(false);
              setActiveSessionIsQualification(false);
              setShowNewCompetitionSessionForm(true);
              setTimeout(() => editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            }}
            style={{
              marginTop: '0',
              maxWidth: isMobile ? 'none' : '450px',
              width: isMobile ? '100%' : undefined,
              whiteSpace: 'nowrap'
            }}
          >
            IMPORT COMPETITION RESULTS
          </SubmitButton>
          <QualificationButton
            onClick={() => {
              setEditingSessionId(null);
              setEditingSessionData(null);
              setIsQualificationMode(true);
              setActiveSessionIsQualification(true);
              setShowNewCompetitionSessionForm(true);
              setTimeout(() => editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            }}
            style={{
              marginTop: '0',
              maxWidth: isMobile ? 'none' : '450px',
              width: isMobile ? '100%' : undefined,
              whiteSpace: 'nowrap'
            }}
          >
            IMPORT QUALIFICATION RESULTS
          </QualificationButton>
        </div>
      ) : (
        <FormAreaWrapper ref={editFormRef}>
        <FormSection>
          <FormTitle>
            {editingSessionId
              ? 'Edit competition session'
              : isQualificationMode
                ? 'Start importing qualification results'
                : 'Start importing competition results'}
          </FormTitle>
        <form onSubmit={handleNewCompetitionSessionSubmit}>
          <FormGrid>
            <FormGroup>
              <Label>Date</Label>
              <Input
                type="date"
                name="date"
                value={editingSessionId && editingSessionData ? editingSessionData.date : newCompetitionSessionData.date}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, date: e.target.value });
                  } else {
                    handleNewCompetitionSessionChange(e);
                  }
                }}
                required
                disabled={isCompetitionSessionActive}
                style={{ 
                  opacity: isCompetitionSessionActive ? 0.6 : 1,
                  backgroundColor: isCompetitionSessionActive ? '#f5f5f5' : '#fff'
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Start Time</Label>
              <Input
                type="time"
                name="startTime"
                value={editingSessionId && editingSessionData ? editingSessionData.startTime : newCompetitionSessionData.startTime}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, startTime: e.target.value });
                  } else {
                    handleNewCompetitionSessionChange(e);
                  }
                }}
                required
                disabled={isCompetitionSessionActive}
                style={{ 
                  opacity: isCompetitionSessionActive ? 0.6 : 1,
                  backgroundColor: isCompetitionSessionActive ? '#f5f5f5' : '#fff'
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>
                Expected End Time
                {error === 'invalid_end_time' && (
                  <span style={{ 
                    color: '#ff0000', 
                    marginLeft: '0.5rem',
                    fontSize: '1.2em'
                  }}>
                    ⚠️
                  </span>
                )}
              </Label>
              <Input
                type="time"
                name="endTime"
                value={editingSessionId && editingSessionData ? editingSessionData.endTime : newCompetitionSessionData.endTime}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, endTime: e.target.value });
                  } else {
                    handleNewCompetitionSessionChange(e);
                  }
                }}
                required
                disabled={isCompetitionSessionActive}
                style={{ 
                  opacity: isCompetitionSessionActive ? 0.6 : 1,
                  backgroundColor: isCompetitionSessionActive ? '#f5f5f5' : '#fff',
                  border: error === 'invalid_end_time' ? '2px solid #ff0000' : undefined
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Weather</Label>
              <Select
                name="weather"
                value={editingSessionId && editingSessionData ? editingSessionData.weather : newCompetitionSessionData.weather}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, weather: e.target.value });
                  } else {
                    handleNewCompetitionSessionChange(e);
                  }
                }}
                required
                disabled={isCompetitionSessionActive}
                style={{ 
                  opacity: isCompetitionSessionActive ? 0.6 : 1,
                  backgroundColor: isCompetitionSessionActive ? '#f5f5f5' : '#fff',
                  color: (editingSessionId && editingSessionData ? editingSessionData.weather : newCompetitionSessionData.weather) === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                  WebkitTextFillColor: (editingSessionId && editingSessionData ? editingSessionData.weather : newCompetitionSessionData.weather) === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                }}
              >
                <option value="">Select weather</option>
                <option value="hot">HOT</option>
                <option value="sunny">SUNNY</option>
                <option value="moderately_cloudy">MODERATELY CLOUDY</option>
                <option value="cloudy">CLOUDY</option>
                <option value="windy">WINDY</option>
                <option value="drizzle">DRIZZLE</option>
                <option value="light_rain">LIGHT RAIN</option>
                <option value="heavy_rain">HEAVY RAIN</option>
              </Select>
            </FormGroup>
          </FormGrid>

          <FormGrid>
            <FormGroup>
              <Label>Location</Label>
              <Input
                type="text"
                name="location"
                value={editingSessionId && editingSessionData ? editingSessionData.location : newCompetitionSessionData.location}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, location: e.target.value });
                  } else {
                    handleNewCompetitionSessionChange(e);
                  }
                }}
                placeholder="Enter competition location"
                required
                disabled={isCompetitionSessionActive}
                style={{ 
                  opacity: isCompetitionSessionActive ? 0.6 : 1,
                  backgroundColor: isCompetitionSessionActive ? '#f5f5f5' : '#fff',
                  color: (editingSessionId && editingSessionData ? editingSessionData.location : newCompetitionSessionData.location) === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                  WebkitTextFillColor: (editingSessionId && editingSessionData ? editingSessionData.location : newCompetitionSessionData.location) === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Hill Size</Label>
              <Input
                type="text"
                name="hillSize"
                value={editingSessionId && editingSessionData ? editingSessionData.hillSize : newCompetitionSessionData.hillSize}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, hillSize: e.target.value });
                  } else {
                    handleNewCompetitionSessionChange(e);
                  }
                }}
                placeholder="Enter hill size (e.g., HS 140)"
                disabled={isCompetitionSessionActive}
                style={{ 
                  opacity: isCompetitionSessionActive ? 0.6 : 1,
                  backgroundColor: isCompetitionSessionActive ? '#f5f5f5' : '#fff',
                  color: (editingSessionId && editingSessionData ? editingSessionData.hillSize : newCompetitionSessionData.hillSize) === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                  WebkitTextFillColor: (editingSessionId && editingSessionData ? editingSessionData.hillSize : newCompetitionSessionData.hillSize) === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Category</Label>
              <Select
                name="category"
                value={editingSessionId && editingSessionData ? editingSessionData.category : newCompetitionSessionData.category}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ 
                      ...editingSessionData, 
                      category: e.target.value,
                      ageCategory: (e.target.value !== 'national_championships' && e.target.value !== 'national_cup') ? '' : editingSessionData.ageCategory
                    });
                  } else {
                    handleNewCompetitionSessionChange(e);
                    if (e.target.value !== 'national_championships' && e.target.value !== 'national_cup') {
                      setNewCompetitionSessionData(prev => ({ ...prev, ageCategory: '' }));
                    }
                  }
                }}
                required
                disabled={isCompetitionSessionActive}
                style={{ 
                  opacity: isCompetitionSessionActive ? 0.6 : 1,
                  backgroundColor: isCompetitionSessionActive ? '#f5f5f5' : '#fff',
                  color: (editingSessionId && editingSessionData ? editingSessionData.category : newCompetitionSessionData.category) === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                  WebkitTextFillColor: (editingSessionId && editingSessionData ? editingSessionData.category : newCompetitionSessionData.category) === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                }}
              >
                <option value="">Select category</option>
                <option value="olympic_games">OLYMPIC GAMES</option>
                <option value="world_championships">WORLD CHAMPIONSHIPS</option>
                <option value="world_cup">WORLD CUP</option>
                <option value="summer_grand_prix">SUMMER GRAND PRIX</option>
                <option value="continental_cup">CONTINENTAL CUP</option>
                <option value="fis_cup">FIS CUP</option>
                <option value="alpen_cup">ALPEN CUP</option>
                <option value="national_championships">NATIONAL CHAMPIONSHIPS</option>
                <option value="national_cup">NATIONAL CUP</option>
                <option value="junior_world_championships">JUNIOR WORLD CHAMPIONSHIPS</option>
                <option value="youth_olympic_games">YOUTH OLYMPIC GAMES</option>
                <option value="eyof">EYOF</option>
                <option value="opa_games">OPA GAMES</option>
              </Select>
            </FormGroup>

            {((editingSessionId && editingSessionData ? editingSessionData.category : newCompetitionSessionData.category) === 'national_championships' || (editingSessionId && editingSessionData ? editingSessionData.category : newCompetitionSessionData.category) === 'national_cup') && (
              <FormGroup>
                <Label>Age Category</Label>
                <Select
                  name="ageCategory"
                  value={editingSessionId && editingSessionData ? editingSessionData.ageCategory : newCompetitionSessionData.ageCategory}
                  onChange={(e) => {
                    if (editingSessionId && editingSessionData) {
                      setEditingSessionData({ ...editingSessionData, ageCategory: e.target.value });
                    } else {
                      handleNewCompetitionSessionChange(e);
                    }
                  }}
                  required
                  disabled={isCompetitionSessionActive}
                  style={{ 
                    opacity: isCompetitionSessionActive ? 0.6 : 1,
                    backgroundColor: isCompetitionSessionActive ? '#f5f5f5' : '#fff',
                    color: (editingSessionId && editingSessionData ? editingSessionData.ageCategory : newCompetitionSessionData.ageCategory) === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                    WebkitTextFillColor: (editingSessionId && editingSessionData ? editingSessionData.ageCategory : newCompetitionSessionData.ageCategory) === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                  }}
                >
                  <option value="">Select age category</option>
                  <option value="adults">ADULTS</option>
                  <option value="u20">U20</option>
                  <option value="u18">U18</option>
                  <option value="u16">U16</option>
                  <option value="u15">U15</option>
                  <option value="u14">U14</option>
                  <option value="u13">U13</option>
                  <option value="u12">U12</option>
                  <option value="u11">U11</option>
                  <option value="u10">U10</option>
                  <option value="u9">U9</option>
                </Select>
              </FormGroup>
            )}
          </FormGrid>

          {!filterAthleteId && (
            <FormGrid>
              <FormGroup style={{ gridColumn: 'span 2' }}>
                <Label>
                  Participating Athletes
                  {error === 'no_athletes_selected' && (
                    <span style={{ 
                      color: '#ff0000', 
                      marginLeft: '0.5rem',
                      fontSize: '1.2em'
                    }}>
                      ⚠️
                    </span>
                  )}
                </Label>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                  background: '#fff',
                  border: error === 'no_athletes_selected' ? '2px solid #ff0000' : undefined
                }}>
                  {athletes.map(athlete => {
                    const currentAthleteIds = editingSessionId && editingSessionData ? editingSessionData.athleteIds : newCompetitionSessionData.athleteIds;
                    const isSelected = currentAthleteIds.some(athleteId => {
                      if (typeof athleteId === 'string') {
                        return athleteId === athlete._id;
                      } else if (athleteId && typeof athleteId === 'object' && '_id' in athleteId) {
                        return (athleteId as PopulatedAthlete)._id === athlete._id;
                      }
                      return false;
                    });
                    const isDisabled = isCompetitionSessionActive || (filterAthleteId ? filterAthleteId !== athlete._id : false);
                    
                    return (
                      <label key={athlete._id} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        padding: '0.25rem',
                        borderRadius: '0.25rem',
                        background: isSelected ? 'var(--accent-orange)' : 'transparent',
                        color: isSelected ? '#fff' : 'var(--text-gray)',
                        opacity: isDisabled && !isSelected ? 0.5 : 1
                      }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            if (isDisabled) return;
                            if (editingSessionId && editingSessionData) {
                              const currentIds = editingSessionData.athleteIds;
                              setEditingSessionData({
                                ...editingSessionData,
                                athleteIds: currentIds.includes(athlete._id)
                                  ? currentIds.filter(id => id !== athlete._id)
                                  : [...currentIds, athlete._id],
                              });
                            } else {
                              handleAthleteSelection(athlete._id);
                            }
                          }}
                          disabled={isDisabled}
                          style={{ margin: 0, opacity: isDisabled ? 0.6 : 1 }}
                        />
                        {athlete.firstName} {athlete.lastName}
                      </label>
                    );
                  })}
                </div>
              </FormGroup>
            </FormGrid>
          )}

          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '1rem',
            ...(isMobile ? { flexDirection: 'column', width: '100%' } : {})
          }}>
            <SubmitButton type="submit" disabled={isCompetitionSessionActive}>
              {editingSessionId ? 'Update Competition Session' : (isCompetitionSessionActive ? 'Competition Session Active' : 'Start Competition Session')}
            </SubmitButton>
            {editingSessionId && (
              <SubmitButton
                type="button"
                onClick={handleCancelEdit}
                style={{ background: '#000', color: '#fff', border: 'none' }}
              >
                Cancel
              </SubmitButton>
            )}
          </div>
        </form>
        </FormSection>
        </FormAreaWrapper>
      )}

      {showJumpForm && (
        <FormAreaWrapper ref={editJumpFormRef}>
        <FormSection>
          <FormTitle>Insert new competition jump</FormTitle>
          {currentSessionIsQualification && (
            <div style={{
              background: 'rgba(23, 162, 184, 0.15)',
              border: '1px solid rgba(23, 162, 184, 0.4)',
              borderRadius: '0.5rem',
              padding: '0.75rem',
              marginBottom: '1rem',
              color: '#0f5c6b',
              fontSize: '0.85rem'
            }}>
              Qualification sessions capture a single official jump per athlete. Trial and second-round inputs are hidden.
            </div>
          )}
        <form onSubmit={handleSubmit}>
          {/* Athlete Selection */}
          <FormGrid style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <FormGroup>
              <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>Athlete</Label>
              <Select
                name="athleteId"
                value={formData.athleteId || filterAthleteId || ''}
                onChange={(e) => {
                  if (!filterAthleteId) {
                    setFormData(prev => ({ ...prev, athleteId: e.target.value }));
                  }
                }}
                required
                disabled={!!filterAthleteId}
                style={{
                  opacity: filterAthleteId ? 0.7 : 1,
                  cursor: filterAthleteId ? 'not-allowed' : 'pointer',
                  color: (formData.athleteId || filterAthleteId || '') === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                  WebkitTextFillColor: (formData.athleteId || filterAthleteId || '') === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                }}
              >
                <option value="">Select Athlete</option>
                {(() => {
                  // Get current session to filter athletes
                  const currentSessionId = (window as any).currentSessionId;
                  const currentSession = currentSessionId 
                    ? competitionSessions.find(s => s._id === currentSessionId)
                    : null;
                  
                  // Filter athletes: if session is active, only show participating athletes (no plan filter – show all, lock by plan)
                  let athletesToShow = athletes;
                  if (currentSession && currentSession.athleteIds && currentSession.athleteIds.length > 0) {
                    athletesToShow = athletes.filter(athlete => {
                      return currentSession.athleteIds.some(athleteId => {
                        if (typeof athleteId === 'string') {
                          return athleteId === athlete._id;
                        } else if (athleteId && typeof athleteId === 'object' && '_id' in athleteId) {
                          return (athleteId as PopulatedAthlete)._id === athlete._id;
                        }
                        return false;
                      });
                    });
                  }
                  
                  // Also apply filterAthleteId if provided
                  if (filterAthleteId) {
                    athletesToShow = athletesToShow.filter(athlete => athlete._id === filterAthleteId);
                  }
                  
                  const requiredPlan = 'hs60';
                  return athletesToShow.map(athlete => {
                    const plan = (athlete as any).subscription?.plan || 'free';
                    const canSelect = hasPlanOrHigher(plan, requiredPlan);
                    return (
                      <option key={athlete._id} value={athlete._id} disabled={!canSelect}>
                        {athlete.firstName} {athlete.lastName}{!canSelect ? ' (Upgrade plan)' : ''}
                      </option>
                    );
                  });
                })()}
              </Select>
              {filterAthleteId && (
                <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem', fontStyle: 'italic' }}>
                  Athlete is automatically selected
                </div>
              )}
            </FormGroup>
          </FormGrid>

          {/* Equipment plan / empty state – desktop: below athlete row; mobile: shown after each column's equipment block */}
          {!isMobile && formData.athleteId && equipmentMessageField && renderEquipmentMessage(equipmentMessageField)}

          {/* Competition form with three vertical columns */}
          <div style={{ 
            marginTop: '1.5rem', 
            marginBottom: '1.5rem',
            display: 'grid',
            gridTemplateColumns: isMobile 
              ? '1fr' 
              : (currentSessionIsQualification ? '1fr' : '1fr 1fr 1fr'),
            gap: isMobile ? '0.75rem' : '1rem',
            alignItems: 'start'
          }}>
            {/* Trial Round Column */}
            {!currentSessionIsQualification && (
              <div style={{ 
                padding: '1rem',
                background: 'var(--bg-sidebar)',
                borderRadius: '0.75rem'
              }}>
                <div style={{ 
                  fontWeight: 700, 
                  fontSize: '0.9rem', 
                  marginBottom: '1rem',
                  color: '#333',
                  fontFamily: "'Poppins', sans-serif"
                }}>
                  Trial Round (Optional)
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>Trial round starting gate:</Label>
                    <Input
                      type="number"
                      inputMode="numeric"
                      name="trialRoundStartingGate"
                      value={formData.trialRoundStartingGate === 0 ? '0' : formData.trialRoundStartingGate}
                      onChange={handleInputChange}
                      min="0"
                      style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: isMobile ? '0.75rem' : '0.9rem', textAlign: isMobile ? 'center' : 'left', color: formData.trialRoundStartingGate === 0 ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined, WebkitTextFillColor: formData.trialRoundStartingGate === 0 ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>Trial round distance:</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      name="trialRoundDistance"
                      value={rawInputValues.trialRoundDistance || (formData.trialRoundDistance > 0 ? String(formData.trialRoundDistance) : '0')}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '-') {
                          setRawInputValues(prev => ({ ...prev, trialRoundDistance: '' }));
                        } else {
                          const normalizedValue = value.replace(',', '.');
                          const numValue = parseFloat(normalizedValue) || 0;
                          setRawInputValues(prev => ({ ...prev, trialRoundDistance: numValue > 0 ? String(numValue) : '' }));
                        }
                      }}
                      style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: isMobile ? '0.75rem' : '0.9rem', textAlign: isMobile ? 'center' : 'left', color: (!rawInputValues.trialRoundDistance && formData.trialRoundDistance === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined, WebkitTextFillColor: (!rawInputValues.trialRoundDistance && formData.trialRoundDistance === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>Trial round inrun speed:</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      name="trialRoundSpeed"
                      value={rawInputValues.trialRoundSpeed || (formData.trialRoundSpeed > 0 ? String(formData.trialRoundSpeed) : '0')}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '-') {
                          setRawInputValues(prev => ({ ...prev, trialRoundSpeed: '' }));
                        } else {
                          const normalizedValue = value.replace(',', '.');
                          const numValue = parseFloat(normalizedValue) || 0;
                          setRawInputValues(prev => ({ ...prev, trialRoundSpeed: numValue > 0 ? String(numValue) : '' }));
                        }
                      }}
                      style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: isMobile ? '0.75rem' : '0.9rem', textAlign: isMobile ? 'center' : 'left', color: (!rawInputValues.trialRoundSpeed && formData.trialRoundSpeed === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined, WebkitTextFillColor: (!rawInputValues.trialRoundSpeed && formData.trialRoundSpeed === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>Trial round wind:</Label>
                    <Input
                      type="text"
                      inputMode="text"
                      name="trialRoundWind"
                      value={formData.trialRoundWind === '' ? '0' : formData.trialRoundWind}
                      onChange={handleInputChange}
                      style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: '0.9rem', color: formData.trialRoundWind === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined, WebkitTextFillColor: formData.trialRoundWind === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                      Trial round suit:
                    </Label>
                    <Select
                      name="trialRoundSuitId"
                      value={formData.trialRoundSuitId}
                      onChange={handleInputChange}
                      onFocus={() => { setEquipmentMessageField('suit'); setEquipmentMessageSection('trialRound'); }}
                      onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: '0.9rem',
                        color: formData.trialRoundSuitId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: formData.trialRoundSuitId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    >
                      <option value="">Select Suit (Optional)</option>
                      {getEquipmentByType('suit', formData.athleteId).map(suit => (
                        <option key={suit._id} value={suit._id}>{suit.name}</option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                      Trial round skis:
                    </Label>
                    <Select
                      name="trialRoundSkisId"
                      value={formData.trialRoundSkisId}
                      onChange={handleInputChange}
                      onFocus={() => { setEquipmentMessageField('skis'); setEquipmentMessageSection('trialRound'); }}
                      onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: '0.9rem',
                        color: formData.trialRoundSkisId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: formData.trialRoundSkisId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    >
                      <option value="">Select Skis (Optional)</option>
                      {getEquipmentByType('skis', formData.athleteId).map(skis => (
                        <option key={skis._id} value={skis._id}>{skis.name}</option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                      Trial round shoes:
                    </Label>
                    <Select
                      name="trialRoundShoesId"
                      value={formData.trialRoundShoesId}
                      onChange={handleInputChange}
                      onFocus={() => { setEquipmentMessageField('shoes'); setEquipmentMessageSection('trialRound'); }}
                      onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: '0.9rem',
                        color: formData.trialRoundShoesId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: formData.trialRoundShoesId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    >
                      <option value="">Select Shoes (Optional)</option>
                      {getEquipmentByType('shoes', formData.athleteId).map(shoes => (
                        <option key={shoes._id} value={shoes._id}>{shoes.name}</option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                      Trial round bindings:
                    </Label>
                    <Select
                      name="trialRoundBindingsId"
                      value={formData.trialRoundBindingsId}
                      onChange={handleInputChange}
                      onFocus={() => { setEquipmentMessageField('bindings'); setEquipmentMessageSection('trialRound'); }}
                      onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: '0.9rem',
                        color: formData.trialRoundBindingsId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: formData.trialRoundBindingsId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    >
                      <option value="">Select Bindings (Optional)</option>
                      {getEquipmentByType('bindings', formData.athleteId).map(bindings => (
                        <option key={bindings._id} value={bindings._id}>{bindings.name}</option>
                      ))}
                    </Select>
                  </FormGroup>
                  {isMobile && equipmentMessageSection === 'trialRound' && equipmentMessageField && renderEquipmentMessage(equipmentMessageField)}

                  <FormGroup style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start', width: '100%' }}>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>Trial round telemark landing:</Label>
                    <TelemarkToggle>
                      <ToggleButton
                        type="button"
                        active={formData.trialRoundTelemark}
                        onClick={() => handleTrialRoundTelemarkToggle(true)}
                      >
                        Yes
                      </ToggleButton>
                      <ToggleButton
                        type="button"
                        active={!formData.trialRoundTelemark}
                        onClick={() => handleTrialRoundTelemarkToggle(false)}
                      >
                        No
                      </ToggleButton>
                    </TelemarkToggle>
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>Rate this jump from 1 to 10 (Optional):</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    name="trialRoundRating"
                    value={formData.trialRoundRating || ''}
                    onChange={handleInputChange}
                    min="1"
                    max="10"
                    step="0.1"
                    placeholder="1-10"
                    style={{
                      width: '100%',
                      padding: '0.35rem 0.5rem',
                      fontSize: isMobile ? '0.75rem' : '0.9rem',
                      textAlign: isMobile ? 'center' : 'left',
                      color: !formData.trialRoundRating ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                      WebkitTextFillColor: !formData.trialRoundRating ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                    }}
                  />
                  </FormGroup>
                </div>
              </div>
            )}

            {/* 1st Round Column */}
            <div style={{ 
              padding: '1rem',
              background: 'var(--bg-sidebar)',
              borderRadius: '0.75rem'
            }}>
              <div style={{ 
                fontWeight: 700, 
                fontSize: '0.9rem', 
                marginBottom: '1rem',
                color: '#333',
                fontFamily: "'Poppins', sans-serif"
              }}>
                1st Round
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <FormGroup>
                  <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                    1st round starting gate:
                  </Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    name="firstRoundStartingGate"
                    value={formData.firstRoundStartingGate === 0 ? '0' : formData.firstRoundStartingGate}
                    onChange={handleInputChange}
                    min="0"
                    required
                    style={{
                      width: '100%',
                      padding: '0.35rem 0.5rem',
                      fontSize: isMobile ? '0.75rem' : '0.9rem',
                      textAlign: isMobile ? 'center' : 'left',
                      color: formData.firstRoundStartingGate === 0 ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                      WebkitTextFillColor: formData.firstRoundStartingGate === 0 ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                    }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>1st round distance:</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    name="firstRoundDistance"
                    value={rawInputValues.firstRoundDistance || (formData.firstRoundDistance > 0 ? String(formData.firstRoundDistance) : '0')}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '-') {
                        setRawInputValues(prev => ({ ...prev, firstRoundDistance: '' }));
                      } else {
                        const normalizedValue = value.replace(',', '.');
                        const numValue = parseFloat(normalizedValue) || 0;
                        setRawInputValues(prev => ({ ...prev, firstRoundDistance: numValue > 0 ? String(numValue) : '' }));
                      }
                    }}
                    required
                    style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: isMobile ? '0.75rem' : '0.9rem', textAlign: isMobile ? 'center' : 'left', color: (!rawInputValues.firstRoundDistance && formData.firstRoundDistance === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined, WebkitTextFillColor: (!rawInputValues.firstRoundDistance && formData.firstRoundDistance === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                    1st round inrun speed:
                  </Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    name="firstRoundSpeed"
                    value={rawInputValues.firstRoundSpeed || (formData.firstRoundSpeed > 0 ? String(formData.firstRoundSpeed) : '0')}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '-') {
                        setRawInputValues(prev => ({ ...prev, firstRoundSpeed: '' }));
                      } else {
                        const normalizedValue = value.replace(',', '.');
                        const numValue = parseFloat(normalizedValue) || 0;
                        setRawInputValues(prev => ({ ...prev, firstRoundSpeed: numValue > 0 ? String(numValue) : '' }));
                      }
                    }}
                    required
                    style={{
                      width: '100%',
                      padding: '0.35rem 0.5rem',
                      fontSize: isMobile ? '0.75rem' : '0.9rem',
                      textAlign: isMobile ? 'center' : 'left',
                      color: (!rawInputValues.firstRoundSpeed && formData.firstRoundSpeed === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                      WebkitTextFillColor: (!rawInputValues.firstRoundSpeed && formData.firstRoundSpeed === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                    }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                    1st round wind:
                  </Label>
                  <Input
                    type="text"
                    inputMode="text"
                    name="firstRoundWind"
                    value={formData.firstRoundWind === '' ? '0' : formData.firstRoundWind}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '0.35rem 0.5rem',
                      fontSize: '0.9rem',
                      color: formData.firstRoundWind === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                      WebkitTextFillColor: formData.firstRoundWind === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                    }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                    1st round suit:
                  </Label>
                  <Select
                    name="firstRoundSuitId"
                    value={formData.firstRoundSuitId}
                    onChange={handleInputChange}
                    onFocus={() => { setEquipmentMessageField('suit'); setEquipmentMessageSection('firstRound'); }}
                    onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                    style={{
                      width: '100%',
                      padding: '0.35rem 0.5rem',
                      fontSize: '0.9rem',
                      color: formData.firstRoundSuitId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                      WebkitTextFillColor: formData.firstRoundSuitId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                    }}
                  >
                    <option value="">Select Suit (Optional)</option>
                    {getEquipmentByType('suit', formData.athleteId).map(suit => (
                      <option key={suit._id} value={suit._id}>{suit.name}</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                    1st round skis:
                  </Label>
                  <Select
                    name="firstRoundSkisId"
                    value={formData.firstRoundSkisId}
                    onChange={handleInputChange}
                    onFocus={() => { setEquipmentMessageField('skis'); setEquipmentMessageSection('firstRound'); }}
                    onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                    style={{
                      width: '100%',
                      padding: '0.35rem 0.5rem',
                      fontSize: '0.9rem',
                      color: formData.firstRoundSkisId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                      WebkitTextFillColor: formData.firstRoundSkisId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                    }}
                  >
                    <option value="">Select Skis (Optional)</option>
                    {getEquipmentByType('skis', formData.athleteId).map(skis => (
                      <option key={skis._id} value={skis._id}>{skis.name}</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                    1st round shoes:
                  </Label>
                  <Select
                    name="firstRoundShoesId"
                    value={formData.firstRoundShoesId}
                    onChange={handleInputChange}
                    onFocus={() => { setEquipmentMessageField('shoes'); setEquipmentMessageSection('firstRound'); }}
                    onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                    style={{
                      width: '100%',
                      padding: '0.35rem 0.5rem',
                      fontSize: '0.9rem',
                      color: formData.firstRoundShoesId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                      WebkitTextFillColor: formData.firstRoundShoesId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                    }}
                  >
                    <option value="">Select Shoes (Optional)</option>
                    {getEquipmentByType('shoes', formData.athleteId).map(shoes => (
                      <option key={shoes._id} value={shoes._id}>{shoes.name}</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                    1st round bindings:
                  </Label>
                  <Select
                    name="firstRoundBindingsId"
                    value={formData.firstRoundBindingsId}
                    onChange={handleInputChange}
                    onFocus={() => { setEquipmentMessageField('bindings'); setEquipmentMessageSection('firstRound'); }}
                    onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                    style={{
                      width: '100%',
                      padding: '0.35rem 0.5rem',
                      fontSize: '0.9rem',
                      color: formData.firstRoundBindingsId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                      WebkitTextFillColor: formData.firstRoundBindingsId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                    }}
                  >
                    <option value="">Select Bindings (Optional)</option>
                    {getEquipmentByType('bindings', formData.athleteId).map(bindings => (
                      <option key={bindings._id} value={bindings._id}>{bindings.name}</option>
                    ))}
                  </Select>
                </FormGroup>
                {isMobile && equipmentMessageSection === 'firstRound' && equipmentMessageField && renderEquipmentMessage(equipmentMessageField)}

                <FormGroup style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start', width: '100%' }}>
                  <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>1st round telemark landing:</Label>
                  <TelemarkToggle>
                    <ToggleButton
                      type="button"
                      active={formData.firstRoundTelemark}
                      onClick={() => handleFirstRoundTelemarkToggle(true)}
                    >
                      Yes
                    </ToggleButton>
                    <ToggleButton
                      type="button"
                      active={!formData.firstRoundTelemark}
                      onClick={() => handleFirstRoundTelemarkToggle(false)}
                    >
                      No
                    </ToggleButton>
                  </TelemarkToggle>
                </FormGroup>

                <FormGroup>
                  <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>Rate this jump from 1 to 10:</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    name="firstRoundRating"
                    value={formData.firstRoundRating === 0 ? '' : formData.firstRoundRating}
                    onChange={handleInputChange}
                    min="1"
                    max="10"
                    step="0.1"
                    placeholder="1-10"
                    style={{
                      width: '100%',
                      padding: '0.35rem 0.5rem',
                      fontSize: isMobile ? '0.75rem' : '0.9rem',
                      textAlign: isMobile ? 'center' : 'left',
                      color: !formData.firstRoundRating ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                      WebkitTextFillColor: !formData.firstRoundRating ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                    }}
                  />
                </FormGroup>
              </div>
            </div>

            {/* 2nd Round Column */}
            {!currentSessionIsQualification && (
              <div style={{ 
                padding: '1rem',
                background: 'var(--bg-sidebar)',
                borderRadius: '0.75rem'
              }}>
                <div style={{ 
                  fontWeight: 700, 
                  fontSize: '0.9rem', 
                  marginBottom: '1rem',
                  color: '#333',
                  fontFamily: "'Poppins', sans-serif"
                }}>
                  2nd Round
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                      2nd round starting gate:
                    </Label>
                    <Input
                      type="number"
                      inputMode="numeric"
                      name="secondRoundStartingGate"
                      value={formData.secondRoundStartingGate === 0 ? '0' : formData.secondRoundStartingGate}
                      onChange={handleInputChange}
                      min="0"
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: isMobile ? '0.75rem' : '0.9rem',
                        textAlign: isMobile ? 'center' : 'left',
                        color: formData.secondRoundStartingGate === 0 ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: formData.secondRoundStartingGate === 0 ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>2nd round distance:</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      name="secondRoundDistance"
                      value={rawInputValues.secondRoundDistance || (formData.secondRoundDistance > 0 ? String(formData.secondRoundDistance) : '0')}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '-') {
                          setRawInputValues(prev => ({ ...prev, secondRoundDistance: '' }));
                        } else {
                          const normalizedValue = value.replace(',', '.');
                          const numValue = parseFloat(normalizedValue) || 0;
                          setRawInputValues(prev => ({ ...prev, secondRoundDistance: numValue > 0 ? String(numValue) : '' }));
                        }
                      }}
                      style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: isMobile ? '0.75rem' : '0.9rem', textAlign: isMobile ? 'center' : 'left', color: (!rawInputValues.secondRoundDistance && formData.secondRoundDistance === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined, WebkitTextFillColor: (!rawInputValues.secondRoundDistance && formData.secondRoundDistance === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                      2nd round inrun speed:
                    </Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      name="secondRoundSpeed"
                      value={rawInputValues.secondRoundSpeed || (formData.secondRoundSpeed > 0 ? String(formData.secondRoundSpeed) : '0')}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '-') {
                          setRawInputValues(prev => ({ ...prev, secondRoundSpeed: '' }));
                        } else {
                          const normalizedValue = value.replace(',', '.');
                          const numValue = parseFloat(normalizedValue) || 0;
                          setRawInputValues(prev => ({ ...prev, secondRoundSpeed: numValue > 0 ? String(numValue) : '' }));
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: isMobile ? '0.75rem' : '0.9rem',
                        textAlign: isMobile ? 'center' : 'left',
                        color: (!rawInputValues.secondRoundSpeed && formData.secondRoundSpeed === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: (!rawInputValues.secondRoundSpeed && formData.secondRoundSpeed === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                      2nd round wind:
                    </Label>
                    <Input
                      type="text"
                      inputMode="text"
                      name="secondRoundWind"
                      value={formData.secondRoundWind === '' ? '0' : formData.secondRoundWind}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: '0.9rem',
                        color: formData.secondRoundWind === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: formData.secondRoundWind === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                      2nd round suit:
                    </Label>
                    <Select
                      name="secondRoundSuitId"
                      value={formData.secondRoundSuitId}
                      onChange={handleInputChange}
                      onFocus={() => { setEquipmentMessageField('suit'); setEquipmentMessageSection('secondRound'); }}
                      onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: '0.9rem',
                        color: formData.secondRoundSuitId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: formData.secondRoundSuitId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    >
                      <option value="">Select Suit (Optional)</option>
                      {getEquipmentByType('suit', formData.athleteId).map(suit => (
                        <option key={suit._id} value={suit._id}>{suit.name}</option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                      2nd round skis:
                    </Label>
                    <Select
                      name="secondRoundSkisId"
                      value={formData.secondRoundSkisId}
                      onChange={handleInputChange}
                      onFocus={() => { setEquipmentMessageField('skis'); setEquipmentMessageSection('secondRound'); }}
                      onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: '0.9rem',
                        color: formData.secondRoundSkisId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: formData.secondRoundSkisId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    >
                      <option value="">Select Skis (Optional)</option>
                      {getEquipmentByType('skis', formData.athleteId).map(skis => (
                        <option key={skis._id} value={skis._id}>{skis.name}</option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                      2nd round shoes:
                    </Label>
                    <Select
                      name="secondRoundShoesId"
                      value={formData.secondRoundShoesId}
                      onChange={handleInputChange}
                      onFocus={() => { setEquipmentMessageField('shoes'); setEquipmentMessageSection('secondRound'); }}
                      onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: '0.9rem',
                        color: formData.secondRoundShoesId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: formData.secondRoundShoesId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    >
                      <option value="">Select Shoes (Optional)</option>
                      {getEquipmentByType('shoes', formData.athleteId).map(shoes => (
                        <option key={shoes._id} value={shoes._id}>{shoes.name}</option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>
                      2nd round bindings:
                    </Label>
                    <Select
                      name="secondRoundBindingsId"
                      value={formData.secondRoundBindingsId}
                      onChange={handleInputChange}
                      onFocus={() => { setEquipmentMessageField('bindings'); setEquipmentMessageSection('secondRound'); }}
                      onBlur={() => { setEquipmentMessageField(null); setEquipmentMessageSection(null); }}
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: '0.9rem',
                        color: formData.secondRoundBindingsId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: formData.secondRoundBindingsId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    >
                      <option value="">Select Bindings (Optional)</option>
                      {getEquipmentByType('bindings', formData.athleteId).map(bindings => (
                        <option key={bindings._id} value={bindings._id}>{bindings.name}</option>
                      ))}
                    </Select>
                  </FormGroup>
                  {isMobile && equipmentMessageSection === 'secondRound' && equipmentMessageField && renderEquipmentMessage(equipmentMessageField)}

                  <FormGroup style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start', width: '100%' }}>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>2nd round telemark landing:</Label>
                    <TelemarkToggle>
                      <ToggleButton
                        type="button"
                        active={formData.secondRoundTelemark}
                        onClick={() => handleSecondRoundTelemarkToggle(true)}
                      >
                        Yes
                      </ToggleButton>
                      <ToggleButton
                        type="button"
                        active={!formData.secondRoundTelemark}
                        onClick={() => handleSecondRoundTelemarkToggle(false)}
                      >
                        No
                      </ToggleButton>
                    </TelemarkToggle>
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>Rate this jump from 1 to 10 (Optional):</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      name="secondRoundRating"
                      value={formData.secondRoundRating === 0 ? '' : formData.secondRoundRating}
                      onChange={handleInputChange}
                      min="1"
                      max="10"
                      step="0.1"
                      placeholder="1-10"
                      style={{
                        width: '100%',
                        padding: '0.35rem 0.5rem',
                        fontSize: isMobile ? '0.75rem' : '0.9rem',
                        textAlign: isMobile ? 'center' : 'left',
                        color: !formData.secondRoundRating ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                        WebkitTextFillColor: !formData.secondRoundRating ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                      }}
                    />
                  </FormGroup>
                </div>
              </div>
            )}
          </div>

          {/* Final Result - Below all columns */}
          <div style={{ 
            marginTop: '1.5rem', 
            marginBottom: '1.5rem'
          }}>
            <FormGroup style={{ maxWidth: '300px' }}>
              <Label style={{ fontSize: '0.7rem', marginBottom: '0.3rem' }}>Final Result:</Label>
              <Input
                type="number"
                name="result"
                value={formData.result === 0 ? '' : formData.result}
                onChange={handleInputChange}
                min="1"
                placeholder="Enter final position"
                required
                style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: '0.9rem' }}
              />
            </FormGroup>
          </div>

          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '1rem',
            ...(isMobile ? { flexDirection: 'column', width: '100%' } : {})
          }}>
            <SubmitButton type="submit">
              {editingJumpId ? 'Update Competition Result' : 'Import Competition Result'}
            </SubmitButton>
            {editingJumpId && (
              <SubmitButton
                type="button"
                onClick={() => {
                  setEditingJumpId(null);
                  setShowJumpForm(false);
      setFormData(prev => ({
        ...prev,
        trialRoundStartingGate: 0,
        trialRoundDistance: 0,
        trialRoundWind: '',
        trialRoundSpeed: 0,
        trialRoundTelemark: false,
        trialRoundSuitId: '',
        trialRoundSkisId: '',
        trialRoundShoesId: '',
        trialRoundBindingsId: '',
        firstRoundStartingGate: 0,
        firstRoundDistance: 0,
        firstRoundWind: '',
        firstRoundSpeed: 0,
        firstRoundTelemark: false,
        firstRoundSuitId: '',
        firstRoundSkisId: '',
        firstRoundShoesId: '',
        firstRoundBindingsId: '',
        secondRoundStartingGate: 0,
        secondRoundDistance: 0,
        secondRoundWind: '',
        secondRoundSpeed: 0,
        secondRoundTelemark: false,
        secondRoundSuitId: '',
        secondRoundSkisId: '',
        secondRoundShoesId: '',
        secondRoundBindingsId: '',
        result: 0,
        trialRoundRating: 0,
        firstRoundRating: 0,
        secondRoundRating: 0,
      }));
      setActiveSessionIsQualification(false);
                }}
                style={{ background: '#000', color: '#fff', border: 'none' }}
              >
                Cancel
              </SubmitButton>
            )}
          </div>
        </form>
        
        {isCompetitionSessionActive && (
          <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <FormGroup>
              <Label>Coach's Description / Notes</Label>
              <textarea
                value={coachDescription}
                onChange={(e) => setCoachDescription(e.target.value)}
                placeholder="Write your thoughts about this competition session..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '0.7rem',
                  fontSize: '1rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e0e6ed',
                  fontFamily: 'Poppins, sans-serif',
                  resize: 'vertical'
                }}
              />
            </FormGroup>
          </div>
        )}
        
        {isCompetitionSessionActive && (
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'flex-end', 
            gap: isMobile ? '0.5rem' : '0.5rem',
            marginTop: '1rem',
            marginBottom: '1rem',
            width: '100%'
          }}>
            {currentSessionIdRef && (
              currentSessionIsPaused ? (
                <SubmitButton
                  type="button"
                  onClick={() => handleContinueCompetitionSession(currentSessionIdRef)}
                  style={{
                    background: '#28a745',
                    boxShadow: '0 2px 8px rgba(40, 167, 69, 0.2)',
                    padding: isMobile ? '0.5rem 1rem' : '0.7rem 1.5rem',
                    fontSize: isMobile ? '0.7rem' : '0.85rem'
                  }}
                >
                  CONTINUE SESSION
                </SubmitButton>
              ) : (
                <SubmitButton
                  type="button"
                  onClick={() => handlePauseCompetitionSession(currentSessionIdRef)}
                  style={{
                    background: '#000',
                    color: '#fff',
                    border: '2px solid #000',
                    boxShadow: 'none',
                    padding: isMobile ? '0.5rem 1rem' : '0.7rem 1.5rem',
                    fontSize: isMobile ? '0.7rem' : '0.85rem'
                  }}
                >
                  PAUSE SESSION
                </SubmitButton>
              )
            )}
            <SubmitButton
              type="button"
              onClick={handleFinishCompetitionSession}
              style={{
                background: '#dc3545',
                color: '#fff',
                border: '2px solid #dc3545',
                boxShadow: 'none',
                padding: isMobile ? '0.5rem 1rem' : '0.7rem 1.5rem',
                fontSize: isMobile ? '0.7rem' : '0.85rem'
              }}
            >
              FINISH COMPETITION
            </SubmitButton>
          </div>
        )}
        </FormSection>
        </FormAreaWrapper>
      )}

      {/* Competition Sessions by Category */}
      {getCategoriesWithSessions().map(category => (
        <CompetitionSessionsTable
          key={category}
          title={`${getCategoryDisplayName(category)} Sessions`}
          sessions={getSessionsByCategory(category)}
          columns={category === 'world_cup' ? [
            { field: 'date', label: 'Date' },
            { field: 'category', label: 'Type' },
            { field: 'location', label: 'Location' },
            { field: 'hillSize', label: 'Hill Size' },
            { field: 'weather', label: 'Weather' },
            { field: 'distance', label: 'Jumps' },
            { field: 'avgDistance', label: 'Avg Distance' }
          ] : [
            { field: 'date', label: 'Date' },
            { field: 'location', label: 'Location' },
            { field: 'hillSize', label: 'Hill Size' },
            { field: 'weather', label: 'Weather' },
            { field: 'distance', label: 'Jumps' },
            { field: 'avgDistance', label: 'Avg Distance' }
          ]}
        />
      ))}

      {/* Banana Cup Sessions Table - Only show if there are Banana Cup sessions */}
      {getSortedBananaCupSessions().length > 0 && <BananaCupSessionsTable />}
      </TrainingSessionContainer>
    </>
  );
};

export default CompetitionSessions;

